// src/app/components/form-builder/FormSettingsTabs.tsx


import React, { useState, useRef, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { Trash2, Plus, Copy, Check, Eye, Code, ChevronDown, ChevronUp, Zap, Smartphone, Globe, RefreshCw, ExternalLink, Code2, Layers, PlayCircle, X } from "lucide-react";
import { generateEmbedKey } from "@/services/formBuilder.service";
import { getStoreInfo } from "@/services/api";
import { Form, NotificationEmail, FormWebhook, MultiLangValue, mlGet, mlSet, mlWrap } from "./types";
import { Language, DEFAULT_LANGUAGES } from "@/types/multilang";

const Inp = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement> & { mono?: boolean }>(
  ({ mono, className, ...p }, ref) => (
    <input ref={ref} {...p} className={`w-full px-3 py-2 min-h-[44px] text-sm border border-ink-200 rounded-lg bg-white font-sans focus:border-brand-500 outline-none transition-colors ${mono ? 'font-mono text-xs' : ''} ${className ?? ''}`} />
  )
);
const Lbl: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <label className="block text-xs font-semibold text-ink-500 mb-1.5 font-sans">{children}</label>
);
const Label: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <label className="block text-xs font-semibold text-ink-500 mb-1.5 font-sans">{children}</label>
);
const Sel: React.FC<React.SelectHTMLAttributes<HTMLSelectElement>> = ({ className, ...p }) => (
  <select {...p} className={`w-full px-3 py-2 min-h-[44px] text-sm border border-ink-200 rounded-lg bg-white font-sans focus:border-brand-500 outline-none ${className ?? ''}`} />
);
const Txt = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement> & { mono?: boolean }>(
  ({ mono, className, ...p }, ref) => (
    <textarea ref={ref} {...p} className={`w-full px-3 py-2 min-h-[44px] text-sm border border-ink-200 rounded-lg bg-white font-sans focus:border-brand-500 outline-none resize-none ${mono ? 'font-mono text-xs' : ''} ${className ?? ''}`} />
  )
);
const Field: React.FC<{ label: string; children: React.ReactNode; className?: string }> = ({ label, children, className }) => (
  <div className={`mb-4 ${className ?? ''}`}><Label>{label}</Label>{children}</div>
);
const Card: React.FC<{ title: string; sub?: string; children: React.ReactNode }> = ({ title, sub, children }) => (
  <div className="bg-white border border-ink-200 rounded-xl p-4 md:p-5 mb-4">
    <p className="text-sm font-semibold text-ink-900 font-sans mb-1">{title}</p>
    {sub && <p className="text-xs text-ink-500 font-sans mb-4">{sub}</p>}
    {children}
  </div>
);
const Toggle: React.FC<{ checked: boolean; onChange: (v: boolean) => void; label: string; sub?: string; disabled?: boolean }> = ({ checked, onChange, label, sub, disabled }) => (
  <div className={`flex items-center justify-between py-2.5 gap-4 ${disabled ? 'opacity-50' : ''}`}>
    <div><p className="text-sm font-medium text-ink-900 font-sans">{label}</p>{sub && <p className="text-xs text-ink-500 font-sans">{sub}</p>}</div>
    <button type="button" disabled={disabled} onClick={() => !disabled && onChange(!checked)} className={`relative flex-shrink-0 transition-colors ${checked ? 'bg-brand-500' : 'bg-borderGreyColor'} ${disabled ? 'cursor-not-allowed' : ''}`}
      style={{ width: 44, height: 24, borderRadius: 12 }}>
      <span className={`absolute top-[2px] w-[20px] h-[20px] bg-white rounded-full shadow transition-all ${checked ? 'left-[22px]' : 'left-[2px]'}`} />
    </button>
  </div>
);
const InfoBox: React.FC<{ children: React.ReactNode; type?: 'info' | 'warn' | 'success' }> = ({ children, type = 'info' }) => {
  const styles = {
    info: 'bg-brand-100 border-brand-500/20 text-brand-600',
    warn: 'bg-amber-50 border-amber-200 text-amber-700',
    success: 'bg-green-50 border-green-200 text-green-700',
  };
  return <div className={`border rounded-lg px-3 py-2.5 text-xs font-sans mb-4 break-words w-full ${styles[type]}`}>{children}</div>;
};
const G2: React.FC<{ children: React.ReactNode }> = ({ children }) => <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>;
const G3: React.FC<{ children: React.ReactNode }> = ({ children }) => <div className="grid grid-cols-1 md:grid-cols-3 gap-3">{children}</div>;

// ─── GENERAL ─────────────────────────────────────────────────────────────────

export const GeneralTab: React.FC<{
  form: Form;
  onChange: (f: Form) => void;
  languages?: Language[];
  activeLang?: string;
}> = ({ form, onChange, languages = DEFAULT_LANGUAGES, activeLang = "en" }) => {
  const set = (k: keyof Form, v: any) => onChange({ ...form, [k]: v });

  // Safely coerce any value to MultiLangValue —
  // handles: plain object, JSON string (from API), plain string (legacy), undefined
  const toML = (v: any): MultiLangValue => {
    if (!v) return { en: '' };
    if (typeof v === 'object') return v as MultiLangValue;
    if (typeof v === 'string') {
      try { const p = JSON.parse(v); if (typeof p === 'object' && p !== null) return p; } catch { }
      return { en: v };
    }
    return { en: '' };
  };

  const setML = (k: keyof Form, lang: string, text: string) => {
    const cur = toML(form[k] as any);
    onChange({ ...form, [k]: { ...cur, [lang]: text } });
  };

  const getLang = (k: keyof Form) => {
    const raw = form[k];
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw);
        if (typeof parsed === 'object' && parsed !== null) {
          const v = parsed[activeLang] ?? parsed['en'] ?? Object.values(parsed)[0];
          return (v !== null && v !== undefined) ? String(v) : '';
        }
      } catch { }
      return raw;
    }
    const ml = toML(raw as any);
    const val = ml[activeLang] ?? ml['en'] ?? Object.values(ml)[0];
    return (val !== null && val !== undefined) ? String(val) : '';
  };

  const langBadge = languages.length > 1
    ? <span className="text-[9px] text-brand-600 bg-brand-100 px-1.5 py-0.5 rounded ml-1 font-sans uppercase">{activeLang}</span>
    : null;

  return (
    <>
      <Card title="Form Details">
        <div className="mb-4">
          <Label>Form Name * {langBadge}</Label>
          <Inp value={getLang('name')} onChange={e => setML('name', activeLang, e.target.value)} placeholder="e.g. Contact Us" />
        </div>
        <G2>
          <div className="mb-4">
            <label className="block text-xs font-semibold text-ink-500 mb-1.5 font-sans">
              Form URL Slug
              <span className="ml-2 text-[10px] font-normal text-ink-500 bg-ink-50 px-1.5 py-0.5 rounded">read-only</span>
            </label>
            <div className="flex items-center gap-2 px-3 py-2 border border-ink-200 rounded-lg bg-ink-50">
              <code className="flex-1 text-xs font-mono text-brand-600 truncate">{form.slug ?? '(saved after first publish)'}</code>
              {form.slug && (
                <button
                  type="button"
                  onClick={() => { navigator.clipboard.writeText(form.slug ?? ''); }}
                  title="Copy slug"
                  className="flex-shrink-0 text-[10px] text-ink-500 hover:text-brand-600 font-sans px-1.5 py-0.5 rounded hover:bg-white transition-colors"
                >
                  Copy
                </button>
              )}
            </div>
            <p className="text-[10px] text-ink-500 mt-1 font-sans">
              Slug is set once when the form is created and cannot be changed — this keeps all embed URLs stable.
            </p>
          </div>
          <Field label="Status">
            <Sel value={form.status} onChange={e => set('status', e.target.value as any)}>
              <option value="active">Active — accepting submissions</option>
              <option value="inactive">Inactive</option>
              <option value="archived">Archived</option>
            </Sel>
          </Field>
        </G2>
        <div className="mb-4">
          <Label>Description {langBadge}</Label>
          <Txt value={getLang('description')} onChange={e => setML('description', activeLang, e.target.value)} rows={2} />
        </div>
      </Card>

      <Card title="Display Options" sub="Control what is shown at the top of your rendered form">
        <Toggle
          checked={form.show_title !== false}
          onChange={v => set('show_title', v)}
          label="Show form title"
          sub="Displays the form name at the top of the rendered form"
        />
        <Toggle
          checked={(form as any).show_description !== false}
          onChange={v => set('show_description' as any, v)}
          label="Show form description"
          sub="Displays the description text below the title"
        />
      </Card>

      <Card title="Page Title Capture" sub="Automatically capture the Shopify/WordPress page title where the form is embedded">
        <Toggle
          checked={!!form.capture_page_title}
          onChange={v => set('capture_page_title', v)}
          label="Capture page title &amp; URL"
          sub="Use {page_title} and {page_url} in emails, or {{ page_title }} in headings"
        />
        {form.capture_page_title && (
          <div className="mt-3 pt-3 border-t border-ink-200">
            <Field label="Hidden field name (for page title)">
              <Inp
                mono
                value={form.page_title_field_name ?? 'page_title'}
                onChange={e => set('page_title_field_name', e.target.value.replace(/[^a-z0-9_]/g, '_'))}
                placeholder="page_title"
              />
            </Field>
            <InfoBox type="info">
              The embed script reads the parent page title automatically.
              Use <code>{'{page_title}'}</code> in emails · <code>{'{{ page_title }}'}</code> in headings
            </InfoBox>
          </div>
        )}
      </Card>

      <Card title="Submission Behaviour">
        <G2>
          <div className="mb-4">
            <Label>Submit Button Text {langBadge}</Label>
            <Inp value={getLang('submit_btn_text')} onChange={e => setML('submit_btn_text', activeLang, e.target.value)} />
          </div>
          <div className="mb-4">
            <Label>Loading Text {langBadge}</Label>
            <Inp value={getLang('submit_btn_loading_text')} onChange={e => setML('submit_btn_loading_text', activeLang, e.target.value)} />
          </div>
        </G2>
        <div className="mb-4">
          <Label>Success Message (HTML) {langBadge}</Label>
          <Txt mono value={getLang('success_message')} onChange={e => setML('success_message', activeLang, e.target.value)} rows={4} placeholder="<p><strong>Thank you!</strong></p>" />
        </div>
        <Field label="Redirect URL (overrides success message)">
          <Inp type="url" value={form.redirect_url ?? ''} onChange={e => set('redirect_url', e.target.value)} placeholder="https://yourstore.com/thank-you" />
        </Field>
        <Toggle checked={form.store_submissions} onChange={v => set('store_submissions', v)} label="Store submissions in database" sub="View and manage in Submissions tab" />
        <Toggle checked={form.is_multi_step} onChange={v => set('is_multi_step', v)} label="Enable multi-step form" sub="Split form into multiple pages with progress" />
      </Card>

      <Card title="Auto-Responder" sub="Send an automatic reply email to the person who submits">
        <Toggle checked={form.auto_responder_enabled} onChange={v => set('auto_responder_enabled', v)} label="Enable auto-responder" />
        {form.auto_responder_enabled && (
          <div className="mt-4 space-y-4 pt-4 border-t border-ink-200">
            <G2>
              <Field label="From Name"><Inp value={form.auto_responder_from_name ?? ''} onChange={e => set('auto_responder_from_name', e.target.value)} placeholder="Store Support Team" /></Field>
              <div className="mb-4">
                <Label>Subject {langBadge}</Label>
                <Inp value={getLang('auto_responder_subject')} onChange={e => setML('auto_responder_subject', activeLang, e.target.value)} placeholder="Thank you, {name}!" />
              </div>
            </G2>
            <div className="mb-4">
              <Label>Email Body (HTML + {'{field_name}'} variables) {langBadge}</Label>
              <Txt mono value={getLang('auto_responder_template')} onChange={e => setML('auto_responder_template', activeLang, e.target.value)} rows={10} placeholder={"<p>Hi {name},</p>\n<p>Thank you for reaching out. We'll reply within 24 hours.</p>"} />
            </div>
          </div>
        )}
      </Card>
    </>
  );
};

// ─── NOTIFICATIONS ────────────────────────────────────────────────────────────

// ─── AUTO VARIABLES ──────────────────────────────────────────────────────────
const AUTO_VARS = [
  { label: 'Form Name', var: 'form_name' },
  { label: 'Store Name', var: 'store_name' },
  { label: 'Page Title', var: 'page_title' },
  { label: 'Page URL', var: 'page_url' },
  { label: 'Submitted At', var: 'submitted_at' },
  { label: 'Submission ID', var: 'submission_id' },
  { label: 'IP Address', var: 'ip_address' },
  { label: 'Current Year', var: 'current_year' },
];

// ─── SAFE LABEL RESOLVER ─────────────────────────────────────────────────────
function resolveLabel(label: any, fieldName = ''): string {
  if (!label) return fieldName || '';
  if (typeof label === 'string') {
    try {
      const p = JSON.parse(label);
      if (typeof p === 'object' && p !== null) {
        const v = p['en'] ?? p['de'] ?? p['ar'] ?? Object.values(p).find((x: any) => x !== null && x !== '');
        return (v !== null && v !== undefined) ? String(v) : fieldName || label;
      }
    } catch { }
    return label || fieldName || '';
  }
  if (typeof label === 'object') {
    const v = (label as any)['en'] ?? (label as any)['de'] ?? (label as any)['ar']
      ?? Object.values(label as any).find((x: any) => x !== null && x !== '');
    return (v !== null && v !== undefined) ? String(v) : fieldName || '';
  }
  return String(label) || fieldName || '';
}

// ─── FONT FAMILY MAP ─────────────────────────────────────────────────────────
const FONT_MAP: Record<string, string> = {
  system: "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif",
  inter: "Inter,'Helvetica Neue',Arial,sans-serif",
  roboto: "Roboto,'Helvetica Neue',Arial,sans-serif",
  'open-sans': "'Open Sans','Helvetica Neue',Arial,sans-serif",
  lato: "Lato,'Helvetica Neue',Arial,sans-serif",
  poppins: "Poppins,'Helvetica Neue',Arial,sans-serif",
};


// ─── GENERATE DEFAULT EMAIL TEMPLATE (icon-free, polished list-card style) ──
function generateDefaultTemplate(
  fields: Form['fields'],
  accentColor = 'var(--brand-500)',
  btnColor = 'var(--brand-500)',
  btnTextColor = '#ffffff',
  logoUrl = '',
  fontFamily = '',
): string {
  const font = FONT_MAP[fontFamily] ?? FONT_MAP.system;
  const skipTypes = new Set(['heading', 'paragraph', 'divider', 'spacer', 'html']);
  const validFields = (fields || []).filter(f => !skipTypes.has(f.type));

  // Email clients can't resolve CSS vars — fall back to a safe orange if one is passed
  const isCssVar = (c: string) => /var\(/.test(c);
  const accent = isCssVar(accentColor) ? '#f97316' : accentColor;

  // Blend a hex color toward white — used for the soft badge background
  const tint = (hex: string, amount: number): string => {
    const m = /^#([0-9a-f]{6})$/i.exec(hex);
    if (!m) return '#fef3ec';
    const num = parseInt(m[1], 16);
    const r = num >> 16, g = (num >> 8) & 0xff, b = num & 0xff;
    const nr = Math.round(r + (255 - r) * amount);
    const ng = Math.round(g + (255 - g) * amount);
    const nb = Math.round(b + (255 - b) * amount);
    return `#${(0x1000000 + nr * 0x10000 + ng * 0x100 + nb).toString(16).slice(1)}`;
  };
  const badgeBg = tint(accent, 0.88);

  // Each field renders as a "list card" row: colored left stripe + uppercase
  // micro-label + bold value. No icons — just color and typography.
  const dataRows = validFields
    .map((f, i) => {
      const lbl = resolveLabel(f.label, f.name);
      const isLast = i === validFields.length - 1;
      return `      <tr>
        <td style="padding:0;${isLast ? '' : 'border-bottom:1px solid #f0f1f5;'}">
          <table width="100%" cellpadding="0" cellspacing="0" role="presentation"><tr>
            <td width="4" bgcolor="${accent}" style="width:4px;line-height:1px;font-size:1px;">&nbsp;</td>
            <td style="padding:12px 16px;">
              <p style="margin:0 0 3px;font-size:10px;font-weight:700;letter-spacing:0.05em;text-transform:uppercase;color:#8a94a6;font-family:${font};">${lbl}</p>
              <p style="margin:0;font-size:13px;font-weight:600;color:#12131a;font-family:${font};word-break:break-word;">{${f.name}}</p>
            </td>
          </tr></table>
        </td>
      </tr>`;
    }).join('\n');

  const logoBlock = logoUrl
    ? `<tr><td style="padding:24px 32px 0 32px;"><img src="${logoUrl}" alt="Logo" style="max-height:30px;max-width:140px;object-fit:contain;" /></td></tr>`
    : '';

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width,initial-scale=1">
  <meta name="color-scheme" content="light">
</head>
<body style="margin:0;padding:0;background:#eef0f4;font-family:${font};-webkit-font-smoothing:antialiased;">
  <table width="100%" cellpadding="0" cellspacing="0" role="presentation" style="background:#eef0f4;padding:24px 16px;">
    <tr><td align="center">
      <table width="580" cellpadding="0" cellspacing="0" role="presentation" style="max-width:580px;width:100%;background:#ffffff;border-radius:16px;overflow:hidden;box-shadow:0 10px 28px rgba(15,23,42,0.07);">

${logoBlock}

        <!-- Header: pill badge + form name + underline accent -->
        <tr><td style="padding:${logoUrl ? '18px' : '30px'} 32px 0 32px;">
          <span style="display:inline-block;padding:5px 11px;border-radius:5px;background:${badgeBg};color:${accent};font-size:10px;font-weight:800;letter-spacing:0.08em;font-family:${font};">NEW SUBMISSION</span>
          <h1 style="margin:5px 0 0;color:#12131a;font-size:23px;font-weight:800;letter-spacing:-0.01em;font-family:${font};">{form_name}</h1>
          <div style="margin-top:10px;width:44px;height:3px;background:${accent};border-radius:3px;"></div>
        </td></tr>

        <!-- Meta strip: Submitted At / Page -->
        <tr><td style="padding:20px 32px 0 32px;">
          <table width="100%" cellpadding="0" cellspacing="0" role="presentation" style="background:#f8f9fc;border:1px solid #eef0f5;border-radius:12px;">
            <tr>
              <td width="50%" style="padding:14px 16px;border-right:1px solid #eaecf1;">
                <p style="margin:0;font-size:10px;font-weight:700;letter-spacing:0.05em;text-transform:uppercase;color:#8a94a6;font-family:${font};">Submitted At</p>
                <p style="margin:4px 0 0;font-size:13px;font-weight:700;color:#12131a;font-family:${font};">{submitted_at}</p>
              </td>
              <td width="50%" style="padding:14px 16px;">
                <p style="margin:0;font-size:10px;font-weight:700;letter-spacing:0.05em;text-transform:uppercase;color:#8a94a6;font-family:${font};">Page Title</p>
                <p style="margin:4px 0 0;font-size:13px;font-weight:700;color:#12131a;font-family:${font};word-break:break-word;">{page_title}</p>
              </td>
            </tr>
          </table>
        </td></tr>

        <!-- Submission Details heading -->
        <tr><td style="padding:24px 32px 0 32px;">
          <table width="100%" cellpadding="0" cellspacing="0" role="presentation"><tr>
            <td style="white-space:nowrap;padding-right:14px;">
              <h2 style="margin:0;font-size:16px;font-weight:800;color:#12131a;font-family:${font};">Submission Details</h2>
            </td>
            <td style="width:100%;border-top:1px solid #eaecf1;font-size:0;">&nbsp;</td>
          </tr></table>
          <div style="margin-top:6px;width:34px;height:2px;background:${accent};border-radius:2px;"></div>
        </td></tr>

        <!-- Field rows: colored left stripe + label + value, no icons -->
        <tr><td style="padding:14px 32px 0 32px;">
          <table width="100%" cellpadding="0" cellspacing="0" role="presentation" style="border:1px solid #eef0f5;border-radius:12px;overflow:hidden;">
${dataRows}
          </table>
        </td></tr>

        <!-- Footer -->
        <tr><td style="padding:26px 32px 24px 32px;">
          <div style="border-top:1px solid #f0d9c8;padding-top:16px;">
            <p style="margin:0;text-align:center;font-size:11px;color:#8a94a6;font-family:${font};">
              This email was sent automatically from your form.
            </p>
          </div>
        </td></tr>

      </table>
    </td></tr>
  </table>
</body>
</html>`;
}
// ─── VAR CHIP ─────────────────────────────────────────────────────────────────
const VarChip: React.FC<{
  label: any;
  varName: string;
  onInsert: (v: string) => void;
  color?: 'purple' | 'blue';
}> = ({ label, varName, onInsert, color = 'blue' }) => {
  const [copied, setCopied] = React.useState(false);
  const safeLabel = resolveLabel(label, varName);
  const cls = color === 'purple'
    ? 'bg-brand-100 text-brand-600 border-[#c4b5fd] hover:bg-[#ddd6fe]'
    : 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100';
  return (
    <button type="button"
      onClick={() => { onInsert(varName); setCopied(true); setTimeout(() => setCopied(false), 1000); }}
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border text-[11px] font-mono font-medium transition-all active:scale-95 ${cls}`}
      title={`Insert {${varName}}`}>
      {copied ? <Check className="h-2.5 w-2.5" /> : null}
      {`{${varName}}`}
      <span className="font-sans font-normal opacity-60 text-[9px] ml-0.5">{safeLabel}</span>
    </button>
  );
};

// ─── EMAIL EDITOR ─────────────────────────────────────────────────────────────
const EmailEditor: React.FC<{
  emailId: string;
  subject: string;
  body: string;
  fields: Form['fields'];
  onSubject: (v: string) => void;
  onBody: (v: string) => void;
  onGenerate: () => void;
  accentColor?: string;
  btnColor?: string;
  btnTextColor?: string;
  logoUrl?: string;
  fontFamily?: string;
}> = ({ emailId, subject, body, fields, onSubject, onBody, onGenerate,
  accentColor = 'var(--brand-500)', btnColor, btnTextColor, logoUrl, fontFamily }) => {
    const [mode, setMode] = React.useState<'html' | 'preview'>('html');
    const [showVars, setShowVars] = React.useState(true);
    const bodyRef = useRef<HTMLTextAreaElement>(null);
    const subjRef = useRef<HTMLInputElement>(null);

    // Live preview — reflects user HTML, falls back to default
    const previewContent = React.useMemo(() =>
      body || generateDefaultTemplate(
        fields,
        accentColor,
        btnColor || accentColor,
        btnTextColor || '#ffffff',
        logoUrl || '',
        fontFamily || '',
      ),
      [body, fields, accentColor, btnColor, btnTextColor, logoUrl, fontFamily]
    );

    const highlightVars = (html: string) =>
      html.replace(/\{([^}]+)\}/g, (_, k) =>
        `<span style="background:var(--brand-100);color:var(--brand-500);padding:1px 5px;border-radius:3px;font-size:11px;font-weight:600">{${k}}</span>`
      );

    const skipTypes = new Set(['heading', 'paragraph', 'divider', 'spacer', 'html']);
    const fieldVars = (fields || []).filter(f => !skipTypes.has(f.type) && f.name);

    const insertVar = useCallback((varName: string, target: 'body' | 'subject') => {
      const token = `{${varName}}`;
      if (target === 'body') {
        const ta = bodyRef.current;
        if (ta) {
          const s = ta.selectionStart ?? body.length;
          const e = ta.selectionEnd ?? body.length;
          onBody(body.slice(0, s) + token + body.slice(e));
          requestAnimationFrame(() => { ta.focus(); ta.setSelectionRange(s + token.length, s + token.length); });
        } else onBody(body + token);
      } else {
        const inp = subjRef.current;
        if (inp) {
          const s = inp.selectionStart ?? subject.length;
          const e = inp.selectionEnd ?? subject.length;
          onSubject(subject.slice(0, s) + token + subject.slice(e));
          requestAnimationFrame(() => { inp.focus(); inp.setSelectionRange(s + token.length, s + token.length); });
        } else onSubject(subject + token);
      }
    }, [body, subject, onBody, onSubject]);

    return (
      <div className="space-y-4">
        {/* Subject */}
        <div>
          <Label>Subject Line</Label>
          <Inp ref={subjRef} value={subject} onChange={e => onSubject(e.target.value)} placeholder="New submission: {form_name} — {page_title}" />
          <div className="flex flex-wrap gap-1 mt-1.5">
            {[...AUTO_VARS.slice(0, 4), ...fieldVars.slice(0, 4)].map(v => {
              const varName = 'var' in v ? v.var : (v as any).name;
              const lbl = 'label' in v ? String(v.label) : resolveLabel((v as any).label, varName);
              return <VarChip key={varName} label={lbl} varName={varName} color="purple" onInsert={v => insertVar(v, 'subject')} />;
            })}
          </div>
        </div>

        {/* Variables panel */}
        <div className="border border-ink-200 rounded-xl overflow-hidden">
          <button type="button" onClick={() => setShowVars(v => !v)}
            className="w-full flex items-center justify-between px-4 py-3 bg-ink-50/60 text-xs font-semibold text-ink-900 font-sans hover:bg-ink-50">
            <span className="flex items-center gap-2"><Zap className="h-3.5 w-3.5 text-brand-600" /> Variables — click to insert into body</span>
            {showVars ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </button>
          {showVars && (
            <div className="p-4 space-y-3 bg-white">
              <div>
                <p className="text-[10px] font-bold text-ink-500 uppercase tracking-wider font-sans mb-2">System Variables</p>
                <div className="flex flex-wrap gap-1.5">
                  {AUTO_VARS.map(v => <VarChip key={v.var} label={v.label} varName={v.var} color="purple" onInsert={v => insertVar(v, 'body')} />)}
                </div>
              </div>
              {fieldVars.length > 0 && (
                <div>
                  <p className="text-[10px] font-bold text-ink-500 uppercase tracking-wider font-sans mb-2">Form Fields</p>
                  <div className="flex flex-wrap gap-1.5">
                    {fieldVars.map(f => <VarChip key={f.name} label={resolveLabel(f.label, f.name)} varName={f.name} color="blue" onInsert={v => insertVar(v, 'body')} />)}
                  </div>
                </div>
              )}
              <p className="text-[10px] text-ink-500 font-sans">Click any chip to insert at cursor.</p>
            </div>
          )}
        </div>

        {/* Body editor */}
        <div>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-0 sm:items-center justify-between mb-2">
            <Label>Email Template</Label>
            <div className="flex items-center gap-2">
              <button type="button" onClick={onGenerate}
                className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium font-sans bg-brand-500 text-white rounded-lg hover:bg-brand-600">
                <Zap className="h-3 w-3" /> Auto-generate
              </button>
              <div className="flex border border-ink-200 rounded-lg overflow-hidden">
                <button type="button" onClick={() => setMode('html')}
                  className={`flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-medium font-sans transition-colors ${mode === 'html' ? 'bg-brand-500 text-white' : 'text-ink-500 hover:bg-ink-50'}`}>
                  <Code className="h-3 w-3" /> HTML
                </button>
                <button type="button" onClick={() => setMode('preview')}
                  className={`flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-medium font-sans transition-colors ${mode === 'preview' ? 'bg-brand-500 text-white' : 'text-ink-500 hover:bg-ink-50'}`}>
                  <Eye className="h-3 w-3" /> Preview
                </button>
              </div>
            </div>
          </div>
          {mode === 'html' ? (
            <Txt ref={bodyRef} mono value={body} onChange={e => onBody(e.target.value)} rows={16}
              placeholder="Click Auto-generate to create a beautiful email template, or write your own HTML here."
              className="text-[11px]" />
          ) : (
            <div className="border border-ink-200 rounded-lg overflow-hidden bg-[#f3f4f6]">
              <div className="flex items-center justify-between px-3 py-2 bg-[#e5e7eb] border-b border-ink-200">
                <div className="flex items-center gap-2">
                  <div className="flex gap-1"><div className="w-3 h-3 rounded-full bg-red-400" /><div className="w-3 h-3 rounded-full bg-yellow-400" /><div className="w-3 h-3 rounded-full bg-green-400" /></div>
                  <span className="text-[10px] text-ink-500 font-sans">Email Preview</span>
                </div>
                <span className="text-[9px] bg-brand-100 text-brand-600 px-2 py-0.5 rounded-full font-sans font-medium">Live — reflects your HTML</span>
              </div>
              <iframe srcDoc={highlightVars(previewContent)}
                className="w-full border-0" style={{ height: 500 }} title="Email preview" sandbox="allow-same-origin" />
            </div>
          )}
          <p className="text-[10px] text-ink-500 font-sans mt-1.5">
            HTML supported. Use <code className="bg-ink-50 px-1 rounded">{"{"}</code>field_name<code className="bg-ink-50 px-1 rounded">{"}"}</code> to insert values.
          </p>
        </div>
      </div>
    );
  };

// ─── NOTIFICATIONS TAB ────────────────────────────────────────────────────────
export const NotificationsTab: React.FC<{
  form: Form;
  onChange: (f: Form) => void;
  fields?: Form['fields'];
}> = ({ form, onChange, fields = [] }) => {
  const emails = form.notification_emails ?? [];
  const uid = () => Math.random().toString(36).slice(2, 8);

  // Store-level branding from /setting API (saved to localStorage as "global_setting")
  const gs: any = React.useMemo(() => {
    try { return JSON.parse(localStorage.getItem('global_setting') || '{}'); } catch { return {}; }
  }, []);
  const gsColor = gs.color ?? {};

  // Priority: form custom_styles → store global_setting → hardcoded default
  const cs = form.custom_styles as any ?? {};
  const accentColor = cs.primaryColor || gsColor.primaryColor || 'var(--brand-500)';
  const btnColor = cs.btnColor || gsColor.buttonBgColor || accentColor;
  const btnTextColor = cs.btnTextColor || gsColor.buttonTextColor || '#ffffff';
  const logoUrl = cs.logoUrl || gs.logo?.headerLogo?.url || '';
  const fontFamily = cs.fontFamily || gs.font?.fontFamily || 'system';

  const makeDefaultEmail = () => ({
    id: uid(),
    name: `Notification ${emails.length + 1}`,
    to: '',
    from_name: '',
    from_email: '',
    cc: '',
    bcc: '',
    subject: 'New submission: {form_name} — {page_title}',
    reply_to: '{email}',
    body: generateDefaultTemplate(fields || form.fields || [], accentColor, btnColor, btnTextColor, logoUrl, fontFamily),
    active: true,
  });

  const add = () => onChange({ ...form, notification_emails: [...emails, makeDefaultEmail()] });
  const del = (id: string) => onChange({ ...form, notification_emails: emails.filter(e => e.id !== id) });
  const upd = (id: string, k: keyof NotificationEmail, v: any) =>
    onChange({ ...form, notification_emails: emails.map(e => e.id === id ? { ...e, [k]: v } : e) });

  return (
    <>
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-0 sm:items-center justify-between mb-4">
        <div>
          <p className="text-sm font-semibold text-ink-900 font-sans">Email Notifications</p>
          <p className="text-xs text-ink-500 font-sans">Send an email to yourself or your team on each new submission</p>
        </div>
        <button onClick={add} className="flex items-center gap-2 px-3 py-2 bg-brand-500 text-white rounded-lg text-xs font-medium font-sans hover:bg-brand-600">
          <Plus className="h-3.5 w-3.5" /> Add Notification
        </button>
      </div>

      {emails.length === 0 && (
        <div className="bg-white border border-dashed border-ink-200 rounded-xl p-10 text-center">
          <div className="w-12 h-12 bg-brand-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <Zap className="h-5 w-5 text-brand-600" />
          </div>
          <p className="text-sm font-medium text-ink-900 font-sans mb-1">No notifications yet</p>
          <p className="text-xs text-ink-500 font-sans mb-4">Add a notification to receive a beautifully formatted email on every submission.</p>
          <button onClick={add} className="inline-flex items-center gap-2 px-4 py-2 bg-brand-500 text-white rounded-lg text-xs font-medium font-sans hover:bg-brand-600">
            <Plus className="h-3.5 w-3.5" /> Create First Notification
          </button>
        </div>
      )}

      {emails.map(email => (
        <div key={email.id} className="bg-white border border-ink-200 rounded-xl overflow-hidden mb-4">
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-0 sm:items-center justify-between px-5 py-3 bg-ink-50/60 border-b border-ink-200">
            <Inp value={email.name ?? ''} onChange={e => upd(email.id!, 'name', e.target.value)}
              className="!border-0 !bg-transparent !p-0 text-sm font-semibold !outline-none w-auto" placeholder="Notification name" />
            <div className="flex items-center gap-3">
              <Toggle checked={email.active} onChange={v => upd(email.id!, 'active', v)} label="" />
              <button onClick={() => del(email.id!)} className="p-1.5 rounded-lg hover:bg-red-50">
                <Trash2 className="h-3.5 w-3.5 text-red-400" />
              </button>
            </div>
          </div>
          <div className="p-4 md:p-5 space-y-4">
            <G2>
              <Field label="To Email(s)"><Inp value={email.to} onChange={e => upd(email.id!, 'to', e.target.value)} placeholder="admin@store.com" /></Field>
              <Field label="Reply-To"><Inp value={email.reply_to ?? ''} onChange={e => upd(email.id!, 'reply_to', e.target.value)} placeholder="{email}" /></Field>
            </G2>
            <G2>
              <Field label="From Name">
                <Inp value={email.from_name ?? ''} onChange={e => upd(email.id!, 'from_name', e.target.value)} placeholder="Your Store Name" />
              </Field>

              <Field label="From Email">
                <div className="relative">
                  <Inp
                    type="email"
                    value="notify@mobimint.co"
                    readOnly
                    className="pr-10 !bg-[#80808029] text-blue-900 font-medium cursor-not-allowed"
                  />

                  <div className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-500">
                    🔒
                  </div>
                </div>

                <p className="mt-1.5 text-xs text-gray-500">
                  This email is managed by Mobimint and cannot be changed.
                </p>
              </Field>

            </G2>
            <G2>
              <Field label="CC"><Inp value={email.cc ?? ''} onChange={e => upd(email.id!, 'cc', e.target.value)} placeholder="cc@example.com" /></Field>
              <Field label="BCC"><Inp value={email.bcc ?? ''} onChange={e => upd(email.id!, 'bcc', e.target.value)} placeholder="bcc@example.com" /></Field>
            </G2>
            <EmailEditor
              emailId={email.id!}
              subject={email.subject}
              body={email.body}
              fields={fields || form.fields || []}
              onSubject={v => upd(email.id!, 'subject', v)}
              onBody={v => upd(email.id!, 'body', v)}
              onGenerate={() => upd(email.id!, 'body', generateDefaultTemplate(fields || form.fields || [], accentColor, btnColor, btnTextColor, logoUrl, fontFamily))}
              accentColor={accentColor}
              btnColor={btnColor}
              btnTextColor={btnTextColor}
              logoUrl={logoUrl}
              fontFamily={fontFamily}
            />
          </div>
        </div>
      ))}

      <Card title="Attachment Settings">
        <Toggle checked={form.attach_files_to_notification}
          onChange={v => onChange({ ...form, attach_files_to_notification: v })}
          label="Attach uploaded files to notification emails"
          sub="Adds file attachments directly to the notification email" />
      </Card>
    </>
  );
};

// ─── SECURITY ─────────────────────────────────────────────────────────────────

export const SecurityTab: React.FC<{ form: Form; onChange: (f: Form) => void }> = ({ form, onChange }) => {
  const set = (k: keyof Form, v: any) => onChange({ ...form, [k]: v });
  return (
    <>
      <Card title="Default Protections">
        <InfoBox type="success">Honeypot and CSRF are enabled by default on all forms and catch most bot submissions.</InfoBox>
        <Toggle checked={form.honeypot_enabled} onChange={v => set('honeypot_enabled', v)} label="Honeypot field (invisible bot trap)" sub="Adds a hidden field that only bots fill in" />
        <Toggle checked={form.csrf_protection} onChange={v => set('csrf_protection', v)} label="CSRF protection" sub="Validates each submission was made from your store" />
      </Card>

      <Card title="Google reCAPTCHA">
        <Toggle
          checked={form.captcha_enabled}
          onChange={v => onChange({
            ...form,
            captcha_enabled: v,
            // The Version select below only offers v2/v3 — without this, a
            // form whose captcha_type defaults to 'none' shows "v2" visually
            // (browsers fall back to the first <option> when the bound value
            // matches none of them) without ever actually setting it, so the
            // real state stays 'none' until the merchant touches the dropdown.
            captcha_type: v && (!form.captcha_type || form.captcha_type === 'none') ? 'v2' : form.captcha_type,
          })}
          label="Enable reCAPTCHA"
        />
        {form.captcha_enabled && (
          <div className="mt-4 pt-4 border-t border-ink-200 space-y-4">
            <Field label="Version">
              <Sel value={form.captcha_type} onChange={e => set('captcha_type', e.target.value as any)}>
                <option value="v2">v2 — Checkbox (I'm not a robot)</option>
                <option value="v3">v3 — Invisible score-based</option>
              </Sel>
            </Field>
            <G2>
              <Field label="Site Key (public)"><Inp mono value={form.captcha_site_key ?? ''} onChange={e => set('captcha_site_key', e.target.value)} placeholder="6Lc..." /></Field>
              <Field label="Secret Key (private)"><Inp type="password" mono value={form.captcha_secret_key ?? ''} onChange={e => set('captcha_secret_key', e.target.value)} placeholder="6Lc..." /></Field>
            </G2>
          </div>
        )}
      </Card>

      <Card title="Rate Limiting & IP Control">
        <G2>
          <Field label="Max submissions per hour (0 = unlimited)"><Inp type="number" value={form.rate_limit_per_hour} onChange={e => set('rate_limit_per_hour', +e.target.value)} /></Field>
          <Field label="Max file size (MB)"><Inp type="number" value={form.max_file_size_mb} onChange={e => set('max_file_size_mb', +e.target.value)} /></Field>
        </G2>
        <Field label="Blocked IP addresses (one per line)">
          <Txt mono value={(form.ip_blocklist ?? []).join('\n')} onChange={e => set('ip_blocklist', e.target.value.split('\n').map(s => s.trim()).filter(Boolean))} rows={4} placeholder={"192.168.1.1\n10.0.0.0"} />
        </Field>
      </Card>
    </>
  );
};

// ─── STYLE ────────────────────────────────────────────────────────────────────

export const StyleTab: React.FC<{ form: Form; onChange: (f: Form) => void }> = ({ form, onChange }) => {
  const set = (k: keyof Form, v: any) => onChange({ ...form, [k]: v });
  const setS = (k: string, v: any) => onChange({ ...form, custom_styles: { ...form.custom_styles, [k]: v } });

  const THEMES = ['default', 'minimal', 'card', 'bordered'] as const;
  const COLORS: [string, string][] = [
    ['primaryColor', 'Primary Color'], ['btnColor', 'Button Color'],
    ['btnTextColor', 'Button Text'], ['labelColor', 'Label Color'],
    ['borderColor', 'Border Color'], ['formBg', 'Form Background'],
  ];

  return (
    <>
      <Card title="Theme Preset">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 mt-1">
          {THEMES.map(t => (
            <button key={t} onClick={() => set('theme', t)}
              className={`p-3 rounded-xl text-center border-2 transition-all ${form.theme === t ? 'border-brand-500 bg-brand-100/40' : 'border-ink-200 hover:border-brand-500/40'}`}>
              <div className={`h-8 rounded mb-2 ${t === 'minimal' ? 'bg-gray-50' : t === 'card' ? 'bg-white shadow-sm border border-gray-200' : t === 'bordered' ? 'bg-white border-2 border-gray-800' : 'bg-white border border-gray-200'}`} />
              <p className="text-[11px] font-medium capitalize font-sans text-ink-900">{t}</p>
            </button>
          ))}
        </div>
      </Card>

      <Card title="Colors">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {COLORS.map(([k, label]) => (
            <Field key={k} label={label}>
              <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                <input type="color" value={(form.custom_styles as any)?.[k] ?? 'var(--brand-500)'} onChange={e => setS(k, e.target.value)}
                  className="w-full sm:w-9 h-11 sm:h-9 rounded-lg border border-ink-200 cursor-pointer p-1 sm:flex-shrink-0" />
                <Inp mono value={(form.custom_styles as any)?.[k] ?? ''} onChange={e => setS(k, e.target.value)} placeholder="var(--brand-500)" />
              </div>
            </Field>
          ))}
        </div>
        <div className="mt-4 pt-4 border-t border-ink-200">
          <Field label="Logo URL (used in email notifications)">
            <div className="flex gap-3 items-center">
              <Inp
                type="url"
                value={(form.custom_styles as any)?.logoUrl ?? ''}
                onChange={e => setS('logoUrl', e.target.value)}
                placeholder="https://yourstore.com/logo.png"
              />
              {(form.custom_styles as any)?.logoUrl && (
                <img
                  src={(form.custom_styles as any).logoUrl}
                  alt="Logo preview"
                  className="h-9 w-auto max-w-[80px] object-contain rounded border border-ink-200 bg-white p-1 flex-shrink-0"
                  onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              )}
            </div>
            <p className="text-[10px] text-ink-500 mt-1.5 font-sans">Logo appears at the top of auto-generated notification emails.</p>
          </Field>
        </div>
      </Card>

      <Card title="Typography & Spacing">
        <G3>
          <Field label="Font Family">
            <Sel value={form.custom_styles?.fontFamily ?? 'poppins'} onChange={e => setS('fontFamily', e.target.value)}>
              <option value="system">System Default</option>
              <option value="inter">Inter</option>
              <option value="roboto">Roboto</option>
              <option value="open-sans">Open Sans</option>
              <option value="lato">Lato</option>
              <option value="poppins">Poppins</option>
            </Sel>
          </Field>
          <Field label="Font Size (px)"><Inp type="number" value={form.custom_styles?.fontSize ?? 14} onChange={e => setS('fontSize', +e.target.value)} /></Field>
          <Field label="Border Radius (px)"><Inp type="number" value={form.custom_styles?.borderRadius ?? 8} onChange={e => setS('borderRadius', +e.target.value)} /></Field>
        </G3>
        <div className="mt-4">
          <Field label="Form Max Width">
            <div className="flex gap-2 flex-nowrap">
              {/* 1. Dropdown */}
              <div className="flex-shrink-0 min-w-[140px]">
                <Sel
                  value={['', '100%', '480px', '560px', '640px', '720px', '800px'].includes(form.custom_styles?.maxWidth ?? '720px')
                    ? (form.custom_styles?.maxWidth ?? '720px')
                    : '__custom'}
                  onChange={e => {
                    if (e.target.value !== '__custom') setS('maxWidth', e.target.value);
                  }}
                  className="!w-full"
                >
                  <option value="">Full Width</option>
                  <option value="480px">Small (480px)</option>
                  <option value="560px">Medium (560px)</option>
                  <option value="640px">Default (640px)</option>
                  <option value="720px">Large (720px)</option>
                  <option value="800px">X-Large (800px)</option>
                  <option value="__custom">Custom…</option>
                </Sel>
              </div>

              {/* 2. Custom Input Box */}
              <div className="flex-1 min-w-[80px]">
                <Inp
                  value={form.custom_styles?.maxWidth ?? '720px'}
                  onChange={e => setS('maxWidth', e.target.value)}
                  placeholder="e.g. 600px or 80%"
                  className="!w-full"
                />
              </div>
            </div>
          </Field>
        </div>
      </Card>

      <Card title="Form Container">
        <G2>
          <Field label="Wrapper Border Radius (px)">
            <Inp
              type="number"
              min={0}
              max={48}
              value={form.custom_styles?.wrapRadius ?? 18}
              onChange={e => setS('wrapRadius', +e.target.value)}
              placeholder="18"
            />
          </Field>
          <Field label="Padding">
            <Sel value={form.custom_styles?.wrapPadding ?? '32px'} onChange={e => setS('wrapPadding', e.target.value)}>
              <option value="">None</option>
              <option value="12px">Small (12px)</option>
              <option value="20px">Medium (20px)</option>
              <option value="32px">Large (32px)</option>
              <option value="48px">X-Large (48px)</option>
            </Sel>
          </Field>
        </G2>
        <div className="mt-2">
          <Label>Border</Label>
          {/* Changed to flex-nowrap so elements never wrap to the next line */}
          <div className="flex gap-2 flex-nowrap">

            {/* 1. Width Input - Fixed 60px width */}
            <div className="flex-shrink-0" style={{ width: '60px' }}>
              <Inp
                type="number"
                min={0} max={8}
                value={form.custom_styles?.wrapBorderWidth ?? 0}
                onChange={e => setS('wrapBorderWidth', +e.target.value)}
                placeholder="0"
                className="!w-full"
              />
            </div>

            {/* 2. Style Dropdown - Auto width based on text, with a minimum */}
            <div className="flex-shrink-0 min-w-[80px] w-auto">
              <Sel
                value={form.custom_styles?.wrapBorderStyle ?? 'solid'}
                onChange={e => setS('wrapBorderStyle', e.target.value)}
                className="!w-full"
              >
                <option value="solid">Solid</option>
                <option value="dashed">Dashed</option>
                <option value="dotted">Dotted</option>
              </Sel>
            </div>

            {/* 3. Color Picker + Text Input - Takes all remaining space */}
            <div className="flex-1 flex gap-2 items-center min-w-[120px]">
              <input
                type="color"
                value={form.custom_styles?.wrapBorderColor ?? '#e5e7eb'}
                onChange={e => setS('wrapBorderColor', e.target.value)}
                className="h-9 w-9 rounded-lg border border-ink-200 cursor-pointer p-1 flex-shrink-0"
              />
              <Inp
                mono
                value={form.custom_styles?.wrapBorderColor ?? ''}
                onChange={e => setS('wrapBorderColor', e.target.value)}
                placeholder="#e5e7eb"
                className="!w-full"
              />
            </div>

          </div>
        </div>
        <div className="mt-4">
          <Field label="Box Shadow">
            <Sel value={form.custom_styles?.wrapShadow ?? 'sm'} onChange={e => setS('wrapShadow', e.target.value)}>
              <option value="">None</option>
              <option value="sm">Small</option>
              <option value="md">Medium</option>
              <option value="lg">Large</option>
              <option value="xl">X-Large</option>
            </Sel>
          </Field>
        </div>
        <div className="mt-2">
          <Field label="Background Color">
            <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
              <input
                type="color"
                value={form.custom_styles?.wrapBg ?? '#ffffff'}
                onChange={e => setS('wrapBg', e.target.value)}
                className="w-full sm:w-9 h-11 sm:h-9 rounded-lg border border-ink-200 cursor-pointer p-1 sm:flex-shrink-0"
              />
              <Inp
                mono
                value={form.custom_styles?.wrapBg ?? '#ffffff'}
                onChange={e => setS('wrapBg', e.target.value)}
                placeholder="#ffffff"
              />
            </div>
          </Field>
        </div>
      </Card>

      <Card title="Custom CSS">
        <Txt mono value={form.custom_css ?? ''} onChange={e => set('custom_css', e.target.value)} rows={10} placeholder={"/* Custom CSS */\n.wf-form { }\n.wf-field { }\n.wf-submit { }"} />
      </Card>
    </>
  );
};

// ─── INTEGRATIONS ─────────────────────────────────────────────────────────────

export const IntegrationsTab: React.FC<{ form: Form; onChange: (f: Form) => void }> = ({ form, onChange }) => {
  const webhooks = form.webhooks ?? [];
  const uid = () => Math.random().toString(36).slice(2, 8);
  const intg = (form.integrations ?? {}) as Record<string, any>;
  const setIntg = (key: string, val: any) => onChange({ ...form, integrations: { ...intg, [key]: val } });
  const getIntg = (key: string) => (intg[key] ?? {}) as Record<string, any>;
  const setIntgField = (key: string, field: string, val: any) =>
    setIntg(key, { ...getIntg(key), [field]: val });

  // Plan-tier gating — integrations/webhooks/cloud-import silently no-op at
  // submission time on plans that don't include them, so surface that here
  // rather than letting merchants configure something that won't fire.
  const { data: storeInfo } = useQuery({
    queryKey: ["store-settings"],
    queryFn: () => getStoreInfo().then(r => r.data.store),
  });
  const limits = storeInfo?.plan_limits ?? {};
  const integrationsLocked = !limits.integrations_enabled;
  const salesforceLocked = integrationsLocked || !limits.salesforce_enabled;
  const cloudImportLocked = !limits.cloud_import_enabled;

  // Field options for mapping dropdowns
  const fieldOpts = [
    { value: '', label: '— not mapped —' },
    ...(form.fields ?? []).map(f => ({
      value: f.name,
      label: typeof f.label === 'object' ? (f.label as any).en ?? f.name : String(f.label ?? f.name),
    })),
  ];

  // ── Webhook helpers ──────────────────────────────────────────────────────────
  const addWebhook = () => onChange({ ...form, webhooks: [...webhooks, { id: uid(), url: '', secret: '', events: ['form.submitted'], active: true }] });
  const delWebhook = (id: string) => onChange({ ...form, webhooks: webhooks.filter(w => w.id !== id) });
  const updWebhook = (id: string, k: keyof FormWebhook, v: any) =>
    onChange({ ...form, webhooks: webhooks.map(w => w.id === id ? { ...w, [k]: v } : w) });

  const WEBHOOK_EVENTS = [
    { value: 'form.submitted', label: 'Form submitted' },
    { value: 'form.viewed', label: 'Form viewed' },
    { value: 'form.approved', label: 'Submission approved' },
    { value: 'form.rejected', label: 'Submission rejected' },
  ];

  // ── Integration section header ────────────────────────────────────────────────
  const IntgHeader: React.FC<{
    logo: React.ReactNode; name: string; sub: string;
    intgKey: string; badge?: string; locked?: boolean;
    onToggle?: (v: boolean) => void;
  }> = ({ logo, name, sub, intgKey, badge, locked, onToggle }) => (
    <div className="flex flex-col sm:flex-row gap-3 sm:gap-0 sm:items-center justify-between mb-3">
      <div className="flex items-center gap-2.5">
        {logo}
        <div>
          <div className="flex items-center gap-2">
            <p className="text-xs font-semibold text-ink-900 font-sans">{name}</p>
            {locked
              ? <span className="text-[9px] font-bold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Upgrade to enable</span>
              : (badge && <span className="text-[9px] font-bold bg-brand-100 text-brand-600 px-1.5 py-0.5 rounded">{badge}</span>)}
          </div>
          <p className="text-[10px] text-ink-500 font-sans">{sub}</p>
        </div>
      </div>
      <Toggle
        checked={!!getIntg(intgKey).enabled}
        onChange={v => onToggle ? onToggle(v) : setIntgField(intgKey, 'enabled', v)}
        label=""
        disabled={locked}
      />
    </div>
  );

  // ── Build a default Slack template listing every field currently on this form ──
  const buildDefaultSlackTemplate = () => {
    const lines = fieldOpts.filter(o => o.value).map(o => `${o.label}: {${o.value}}`);
    return ['New submission from *{form_name}*', ...lines].join('\n');
  };

  // ── Field mapping select ───────────────────────────────────────────────────────
  const FieldMap: React.FC<{ intgKey: string; field: string; label: string }> = ({ intgKey, field, label }) => (
    <Field label={label}>
      <Sel value={getIntg(intgKey)[field] ?? ''} onChange={e => setIntgField(intgKey, field, e.target.value)}>
        {fieldOpts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </Sel>
    </Field>
  );

  return (
    <>
      {/* ══ WEBHOOKS ══════════════════════════════════════════════════════════ */}
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-0 sm:items-center justify-between mb-4">
        <div>
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold text-ink-900 font-sans">Webhooks</p>
            {integrationsLocked && <span className="text-[9px] font-bold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Upgrade to enable</span>}
          </div>
          <p className="text-xs text-ink-500 font-sans">POST submission data to any URL. Payload signed with HMAC-SHA256.</p>
        </div>
        <button onClick={addWebhook} disabled={integrationsLocked} className={`flex items-center gap-2 px-3 py-2 bg-brand-500 text-white rounded-lg text-xs font-medium font-sans hover:bg-brand-600 ${integrationsLocked ? 'opacity-50 cursor-not-allowed' : ''}`}>
          <Plus className="h-3.5 w-3.5" /> Add Webhook
        </button>
      </div>

      {webhooks.map(wh => (
        <div key={wh.id} className="bg-white border border-ink-200 rounded-xl p-4 md:p-5 mb-4">
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-0 sm:items-center justify-between mb-4">
            <Toggle checked={wh.active} onChange={v => updWebhook(wh.id!, 'active', v)} label="Active" disabled={integrationsLocked} />
            <button onClick={() => delWebhook(wh.id!)} className="p-1.5 rounded-lg hover:bg-red-50">
              <Trash2 className="h-3.5 w-3.5 text-red-400" />
            </button>
          </div>
          <G2>
            <Field label="Endpoint URL">
              <Inp mono value={wh.url} onChange={e => updWebhook(wh.id!, 'url', e.target.value)} placeholder="https://hooks.example.com/..." />
            </Field>
            <Field label="Signing Secret (HMAC-SHA256)">
              <Inp type="password" mono value={wh.secret ?? ''} onChange={e => updWebhook(wh.id!, 'secret', e.target.value)} placeholder="whsec_..." />
            </Field>
          </G2>
          <Field label="Trigger on">
            <div className="flex flex-wrap gap-2 mt-1">
              {WEBHOOK_EVENTS.map(ev => {
                const events: string[] = (wh as any).events ?? ['form.submitted'];
                const active = events.includes(ev.value);
                return (
                  <label key={ev.value} className="flex items-center gap-1.5 cursor-pointer">
                    <input type="checkbox" checked={active} className="accent-[var(--brand-500)]"
                      onChange={() => updWebhook(wh.id!, 'events' as any,
                        active ? events.filter(e => e !== ev.value) : [...events, ev.value])} />
                    <span className="text-xs text-ink-900 font-sans">{ev.label}</span>
                  </label>
                );
              })}
            </div>
          </Field>
          <InfoBox>Header: <code>X-Webhook-Signature: sha256=HMAC(secret, body)</code></InfoBox>
        </div>
      ))}

      {webhooks.length === 0 && (
        <div className="bg-white border border-dashed border-ink-200 rounded-xl p-8 text-center mb-6">
          <p className="text-sm text-ink-500 font-sans">No webhooks yet — click <strong>Add Webhook</strong> above.</p>
        </div>
      )}

      {/* ══ KLAVIYO ════════════════════════════════════════════════════════════ */}
      <div className="bg-white border border-ink-200 rounded-xl p-4 md:p-5 mb-4">
        <IntgHeader
          intgKey="klaviyo"
          locked={integrationsLocked}
          name="Klaviyo"
          sub="Add subscribers to a Klaviyo list on form submission"
          badge="Email + SMS"
          logo={
            <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 font-bold text-white text-[10px]" style={{ background: '#1F2937' }}>Kl</div>
          }
        />
        {getIntg('klaviyo').enabled && (
          <>
            <InfoBox type="info">
              Find your <strong>Private API Key</strong> at <a href="https://www.klaviyo.com/account#api-keys-tab" target="_blank" rel="noreferrer" className="underline">klaviyo.com → Account → API Keys</a>. The <strong>List ID</strong> is under Lists &amp; Segments.
            </InfoBox>
            <G2>
              <Field label="Private API Key">
                <Inp mono type="password" value={getIntg('klaviyo').api_key ?? ''} onChange={e => setIntgField('klaviyo', 'api_key', e.target.value)} placeholder="pk_..." />
              </Field>
              <Field label="List ID">
                <Inp mono value={getIntg('klaviyo').list_id ?? ''} onChange={e => setIntgField('klaviyo', 'list_id', e.target.value)} placeholder="AbCdEf" />
              </Field>
            </G2>
            <p className="text-[10px] font-bold text-ink-500 uppercase tracking-widest mb-3 font-sans">Field Mapping</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FieldMap intgKey="klaviyo" field="email_field" label="Email *" />
              <FieldMap intgKey="klaviyo" field="first_name_field" label="First Name" />
              <FieldMap intgKey="klaviyo" field="last_name_field" label="Last Name" />
              <FieldMap intgKey="klaviyo" field="phone_field" label="Phone (SMS)" />
            </div>
            <Toggle checked={!!getIntg('klaviyo').sms_consent} onChange={v => setIntgField('klaviyo', 'sms_consent', v)}
              label="Subscribe to SMS" sub="Also subscribe the phone number for Klaviyo SMS" />
          </>
        )}
      </div>

      {/* ══ MAILCHIMP ══════════════════════════════════════════════════════════ */}
      <div className="bg-white border border-ink-200 rounded-xl p-4 md:p-5 mb-4">
        <IntgHeader
          intgKey="mailchimp"
          locked={integrationsLocked}
          name="Mailchimp"
          sub="Add contacts to a Mailchimp audience on form submission"
          logo={
            <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 font-bold text-xs" style={{ background: '#FFE01B', color: '#241C15' }}>MC</div>
          }
        />
        {getIntg('mailchimp').enabled && (
          <>
            <InfoBox type="info">
              Get your API key from <a href="https://us1.admin.mailchimp.com/account/api/" target="_blank" rel="noreferrer" className="underline">Mailchimp → Account → API Keys</a>. Your <strong>Server Prefix</strong> (e.g. <code>us1</code>) is in your Mailchimp URL. The <strong>Audience ID</strong> is under Audience → Settings.
            </InfoBox>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-0">
              <div className="col-span-2">
                <Field label="API Key">
                  <Inp mono type="password" value={getIntg('mailchimp').api_key ?? ''} onChange={e => setIntgField('mailchimp', 'api_key', e.target.value)} placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx-us1" />
                </Field>
              </div>
              <Field label="Server Prefix">
                <Inp mono value={getIntg('mailchimp').server_prefix ?? ''} onChange={e => setIntgField('mailchimp', 'server_prefix', e.target.value)} placeholder="us1" />
              </Field>
            </div>
            <Field label="Audience / List ID">
              <Inp mono value={getIntg('mailchimp').list_id ?? ''} onChange={e => setIntgField('mailchimp', 'list_id', e.target.value)} placeholder="a1b2c3d4e5" />
            </Field>
            <p className="text-[10px] font-bold text-ink-500 uppercase tracking-widest mb-3 font-sans">Field Mapping</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FieldMap intgKey="mailchimp" field="email_field" label="Email *" />
              <FieldMap intgKey="mailchimp" field="first_name_field" label="First Name (FNAME)" />
              <FieldMap intgKey="mailchimp" field="last_name_field" label="Last Name (LNAME)" />
              <FieldMap intgKey="mailchimp" field="phone_field" label="Phone (PHONE)" />
            </div>
            <Field label="Tags (comma-separated)">
              <Inp value={getIntg('mailchimp').tags ?? ''} onChange={e => setIntgField('mailchimp', 'tags', e.target.value)} placeholder="shopify, form-lead" />
            </Field>
            <Toggle checked={!!getIntg('mailchimp').double_optin} onChange={v => setIntgField('mailchimp', 'double_optin', v)}
              label="Double opt-in" sub="Send a confirmation email before adding to the list" />
          </>
        )}
      </div>

      {/* ══ HUBSPOT ════════════════════════════════════════════════════════════ */}
      <div className="bg-white border border-ink-200 rounded-xl p-4 md:p-5 mb-4">
        <IntgHeader
          intgKey="hubspot"
          locked={integrationsLocked}
          name="HubSpot"
          sub="Create or update contacts in HubSpot CRM on submission"
          logo={
            <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 font-bold text-white text-[10px]" style={{ background: '#ff7a59' }}>HS</div>
          }
        />
        {getIntg('hubspot').enabled && (
          <>
            <InfoBox type="info">
              Create a <strong>Private App</strong> in HubSpot (Settings → Integrations → Private Apps) with <code>crm.objects.contacts.write</code> scope. Copy the access token below.
            </InfoBox>
            <Field label="Access Token">
              <Inp mono type="password" value={getIntg('hubspot').access_token ?? ''} onChange={e => setIntgField('hubspot', 'access_token', e.target.value)} placeholder="pat-na1-..." />
            </Field>
            <p className="text-[10px] font-bold text-ink-500 uppercase tracking-widest mb-3 font-sans">Field Mapping</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FieldMap intgKey="hubspot" field="email_field" label="Email *" />
              <FieldMap intgKey="hubspot" field="firstname_field" label="First Name" />
              <FieldMap intgKey="hubspot" field="lastname_field" label="Last Name" />
              <FieldMap intgKey="hubspot" field="phone_field" label="Phone" />
              <FieldMap intgKey="hubspot" field="company_field" label="Company" />
              <FieldMap intgKey="hubspot" field="message_field" label="Message / Notes" />
            </div>
          </>
        )}
      </div>

      {/* ══ SLACK ══════════════════════════════════════════════════════════════ */}
      <div className="bg-white border border-ink-200 rounded-xl p-4 md:p-5 mb-4">
        <IntgHeader
          intgKey="slack"
          locked={integrationsLocked}
          name="Slack"
          sub="Post a message to a Slack channel when a form is submitted"
          onToggle={v => {
            const alreadyHasTemplate = !!getIntg('slack').message_template;
            setIntg('slack', {
              ...getIntg('slack'),
              enabled: v,
              message_template: (v && !alreadyHasTemplate) ? buildDefaultSlackTemplate() : getIntg('slack').message_template,
            });
          }}
          logo={
            <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 font-bold text-white text-[10px]" style={{ background: '#4A154B' }}>Sl</div>
          }
        />
        {getIntg('slack').enabled && (
          <>
            <InfoBox type="info">
              Go to <a href="https://api.slack.com/apps" target="_blank" rel="noreferrer" className="underline">api.slack.com/apps</a> → Create App → Incoming Webhooks → Activate → Add to Workspace → copy the Webhook URL.
            </InfoBox>
            <Field label="Incoming Webhook URL">
              <Inp mono value={getIntg('slack').webhook_url ?? ''} onChange={e => setIntgField('slack', 'webhook_url', e.target.value)} placeholder="https://hooks.slack.com/services/T.../B.../..." />
            </Field>
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs font-medium text-ink-900 font-sans">Message Template</p>
              <button type="button" onClick={() => setIntgField('slack', 'message_template', buildDefaultSlackTemplate())}
                className="text-[10px] font-semibold text-brand-600 hover:text-brand-700 font-sans">
                Auto-fill from form fields
              </button>
            </div>
            <Field label="">
              <Txt rows={3} value={getIntg('slack').message_template ?? ''} onChange={e => setIntgField('slack', 'message_template', e.target.value)}
                placeholder="New submission from *{form_name}*&#10;Email: {email}&#10;Message: {message}" />
              <p className="text-[10px] text-ink-500 font-sans mt-1">
                Use <code>{'{'}<em>field_name</em>{'}'}</code> tokens for any field on this form. <code>{'{form_name}'}</code> and <code>{'{submission_id}'}</code> are always available.
              </p>
            </Field>
          </>
        )}
      </div>

      {/* ══ ZAPIER ═════════════════════════════════════════════════════════════ */}
      <div className="bg-white border border-ink-200 rounded-xl p-4 md:p-5 mb-4">
        <IntgHeader
          intgKey="zapier"
          locked={integrationsLocked}
          name="Zapier"
          sub="Trigger a Zap with every new form submission"
          badge="Automation"
          logo={
            <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 font-bold text-white text-[10px]" style={{ background: '#ff4a00' }}>Zp</div>
          }
        />
        {getIntg('zapier').enabled && (
          <>
            <InfoBox type="info">
              In Zapier, create a new Zap → Trigger: <strong>Webhooks by Zapier → Catch Hook</strong> → copy the webhook URL below.
            </InfoBox>
            <Field label="Zapier Webhook URL">
              <Inp mono value={getIntg('zapier').webhook_url ?? ''} onChange={e => setIntgField('zapier', 'webhook_url', e.target.value)} placeholder="https://hooks.zapier.com/hooks/catch/..." />
            </Field>
          </>
        )}
      </div>

      {/* ══ MAKE.COM ═══════════════════════════════════════════════════════════ */}
      <div className="bg-white border border-ink-200 rounded-xl p-4 md:p-5 mb-4">
        <IntgHeader
          intgKey="make"
          locked={integrationsLocked}
          name="Make.com"
          sub="Trigger a Make scenario with every new form submission"
          badge="Automation"
          logo={
            <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 font-bold text-white text-[10px]" style={{ background: '#6D00CC' }}>Mk</div>
          }
        />
        {getIntg('make').enabled && (
          <>
            <InfoBox type="info">
              In Make, add a <strong>Webhooks → Custom webhook</strong> trigger module to your scenario → copy the webhook URL below.
            </InfoBox>
            <Field label="Make.com Webhook URL">
              <Inp mono value={getIntg('make').webhook_url ?? ''} onChange={e => setIntgField('make', 'webhook_url', e.target.value)} placeholder="https://hook.us1.make.com/..." />
            </Field>
          </>
        )}
      </div>

      {/* ══ N8N ════════════════════════════════════════════════════════════════ */}
      <div className="bg-white border border-ink-200 rounded-xl p-4 md:p-5 mb-4">
        <IntgHeader
          intgKey="n8n"
          locked={integrationsLocked}
          name="n8n"
          sub="Trigger an n8n workflow with every new form submission"
          badge="Automation"
          logo={
            <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 font-bold text-white text-[10px]" style={{ background: '#EA4B71' }}>n8</div>
          }
        />
        {getIntg('n8n').enabled && (
          <>
            <InfoBox type="info">
              In n8n, add a <strong>Webhook</strong> trigger node to your workflow → copy its production webhook URL below.
            </InfoBox>
            <Field label="n8n Webhook URL">
              <Inp mono value={getIntg('n8n').webhook_url ?? ''} onChange={e => setIntgField('n8n', 'webhook_url', e.target.value)} placeholder="https://your-instance.app.n8n.cloud/webhook/..." />
            </Field>
          </>
        )}
      </div>

      {/* ══ GOOGLE SHEETS ══════════════════════════════════════════════════════ */}
      <div className="bg-white border border-ink-200 rounded-xl p-4 md:p-5 mb-4">
        <IntgHeader
          intgKey="google_sheets"
          locked={integrationsLocked}
          name="Google Sheets"
          sub="Append a new row to a Google Sheet on every submission"
          logo={
            <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 text-white text-[10px] font-bold" style={{ background: '#0f9d58' }}>GS</div>
          }
        />
        {getIntg('google_sheets').enabled && (
          <>
            <InfoBox type="info">
              Create a <strong>Service Account</strong> in <a href="https://console.cloud.google.com/iam-admin/serviceaccounts" target="_blank" rel="noreferrer" className="underline">Google Cloud Console</a>, enable the <strong>Google Sheets API</strong>, download the JSON key, then share your spreadsheet with the service account email.
            </InfoBox>
            <G2>
              <Field label="Spreadsheet ID">
                <Inp mono value={getIntg('google_sheets').sheet_id ?? ''} onChange={e => setIntgField('google_sheets', 'sheet_id', e.target.value)} placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgVE2upms" />
              </Field>
              <Field label="Tab Name (not the spreadsheet title)">
                <Inp value={getIntg('google_sheets').sheet_name ?? ''} onChange={e => setIntgField('google_sheets', 'sheet_name', e.target.value)} placeholder="Sheet1" />
                <p className="text-[10px] text-ink-500 font-sans mt-1">
                  The tab label at the <strong>bottom-left</strong> of your spreadsheet window (default is <code>Sheet1</code>) — not the file's title at the top of the browser tab. Leave blank to use <code>Sheet1</code>.
                </p>
              </Field>
            </G2>
            <Field label="Service Account JSON (credentials)">
              <Txt rows={4} mono value={getIntg('google_sheets').credentials_json ?? ''} onChange={e => setIntgField('google_sheets', 'credentials_json', e.target.value)} placeholder={'{\n  "type": "service_account",\n  "project_id": "...",\n  ...\n}'} />
            </Field>
            <p className="text-[10px] text-ink-500 font-sans mt-1 mb-3">
              Column headers are created automatically from field names on the first submission. The Spreadsheet ID is in the sheet URL: …/spreadsheets/d/<strong>SPREADSHEET_ID</strong>/edit
            </p>
          </>
        )}
      </div>

      {/* ══ SALESFORCE ═════════════════════════════════════════════════════════ */}
      <div className="bg-white border border-ink-200 rounded-xl p-4 md:p-5 mb-4">
        <IntgHeader
          intgKey="salesforce"
          locked={salesforceLocked}
          name="Salesforce"
          sub="Create Leads in Salesforce CRM on form submission"
          badge="Enterprise"
          logo={
            <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 font-bold text-white text-[10px]" style={{ background: '#00a1e0' }}>SF</div>
          }
        />
        {getIntg('salesforce').enabled && (
          <>
            <InfoBox type="info">
              Create a <strong>Connected App</strong> in Salesforce Setup → App Manager. Enable OAuth with <code>api</code> scope. Use a dedicated integration user with API access.
            </InfoBox>
            <G2>
              <Field label="Consumer Key (Client ID)">
                <Inp mono value={getIntg('salesforce').client_id ?? ''} onChange={e => setIntgField('salesforce', 'client_id', e.target.value)} placeholder="3MVG9..." />
              </Field>
              <Field label="Consumer Secret (Client Secret)">
                <Inp mono type="password" value={getIntg('salesforce').client_secret ?? ''} onChange={e => setIntgField('salesforce', 'client_secret', e.target.value)} placeholder="..." />
              </Field>
              <Field label="Username">
                <Inp value={getIntg('salesforce').username ?? ''} onChange={e => setIntgField('salesforce', 'username', e.target.value)} placeholder="user@example.com" />
              </Field>
              <Field label="Password + Security Token">
                <Inp mono type="password" value={getIntg('salesforce').password ?? ''} onChange={e => setIntgField('salesforce', 'password', e.target.value)} placeholder="PasswordSecurityToken" />
              </Field>
            </G2>
            <p className="text-[10px] font-bold text-ink-500 uppercase tracking-widest mb-3 font-sans">Field Mapping (Lead Object)</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FieldMap intgKey="salesforce" field="email_field" label="Email" />
              <FieldMap intgKey="salesforce" field="firstname_field" label="First Name" />
              <FieldMap intgKey="salesforce" field="lastname_field" label="Last Name (required)" />
              <FieldMap intgKey="salesforce" field="phone_field" label="Phone" />
              <FieldMap intgKey="salesforce" field="company_field" label="Company (required)" />
              <FieldMap intgKey="salesforce" field="description_field" label="Description" />
            </div>
          </>
        )}
      </div>

      {/* ══ CLOUD FILE STORAGE ═════════════════════════════════════════════════ */}
      <Card title="Cloud File Upload" sub="Enable Google Drive and Dropbox pickers on file upload fields in this form.">
        {/* Google Drive */}
        <div className="mb-5 pb-5 border-b border-ink-200">
          <IntgHeader
            intgKey="google_drive"
            locked={cloudImportLocked}
            name="Google Drive"
            sub="Let users pick files from their Google Drive"
            logo={
              <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: '#e8f0fe' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="#1a73e8"><path d="M6.18 15L3.75 19h16.5l-2.43-4H6.18zM12 3L2 19h4.75L12 9.5 17.25 19H22L12 3z" /></svg>
              </div>
            }
          />
          {getIntg('google_drive').enabled && (
            <>
              <p className="text-[10px] text-ink-500 font-sans mb-3">
                Enable Drive API + Picker API and create OAuth 2.0 credentials at{' '}
                <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noreferrer" className="text-brand-600 underline">console.cloud.google.com</a>.
              </p>
              <G2>
                <Field label="Google API Key">
                  <Inp mono value={getIntg('google_drive').api_key ?? ''} onChange={e => setIntgField('google_drive', 'api_key', e.target.value)} placeholder="AIzaSy..." />
                </Field>
                <Field label="OAuth Client ID">
                  <Inp mono value={getIntg('google_drive').client_id ?? ''} onChange={e => setIntgField('google_drive', 'client_id', e.target.value)} placeholder="123456789-xxx.apps.googleusercontent.com" />
                </Field>
              </G2>
              <Field label="OAuth Client Secret">
                <Inp mono type="password" value={getIntg('google_drive').client_secret ?? ''} onChange={e => setIntgField('google_drive', 'client_secret', e.target.value)} placeholder="GOCSPX-..." />
              </Field>
              <InfoBox type="info">Add <strong>https://api-forms.mobimint.co/gdrive/callback</strong> to <strong>Authorised redirect URIs</strong> in your OAuth 2.0 client.</InfoBox>
            </>
          )}
        </div>

        {/* Dropbox */}
        <IntgHeader
          intgKey="dropbox"
          locked={cloudImportLocked}
          name="Dropbox"
          sub="Let users pick files from their Dropbox"
          logo={
            <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: '#dbeafe' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="#0061fe"><path d="M12 2L6 6.5 12 11 6 15.5 0 11l6-4.5L0 2 6 6.5 12 2zM18 2l-6 4.5L18 11l-6 4.5L18 20l6-4.5L18 11l6-4.5L18 2z" /></svg>
            </div>
          }
        />
        {getIntg('dropbox').enabled && (
          <>
            <p className="text-[10px] text-ink-500 font-sans mb-3">
              Create an app at{' '}
              <a href="https://www.dropbox.com/developers/apps" target="_blank" rel="noreferrer" className="text-brand-600 underline">dropbox.com/developers/apps</a>
              {' '}→ Scoped access → Full Dropbox. Add <strong>https://api-forms.mobimint.co/dropbox/callback</strong> as redirect URI.
            </p>
            <G2>
              <Field label="App Key">
                <Inp mono value={getIntg('dropbox').app_key ?? ''} onChange={e => setIntgField('dropbox', 'app_key', e.target.value)} placeholder="xxxxxxxxxxxxxxx" />
              </Field>
              <Field label="App Secret">
                <Inp mono type="password" value={getIntg('dropbox').app_secret ?? ''} onChange={e => setIntgField('dropbox', 'app_secret', e.target.value)} placeholder="xxxxxxxxxxxxxxx" />
              </Field>
            </G2>
          </>
        )}
      </Card>
    </>
  );
};

// ─── GDPR ─────────────────────────────────────────────────────────────────────

export const GdprTab: React.FC<{ form: Form; onChange: (f: Form) => void }> = ({ form, onChange }) => {
  const set = (k: keyof Form, v: any) => onChange({ ...form, [k]: v });
  return (
    <Card title="GDPR Compliance" sub="Manage data privacy and user consent for European regulations">
      <Toggle checked={form.gdpr_consent_required} onChange={v => set('gdpr_consent_required', v)} label="Require consent checkbox" sub="User must agree before they can submit the form" />
      {form.gdpr_consent_required && (
        <Field label="Consent Text">
          <Txt value={form.gdpr_consent_text ?? ''} onChange={e => set('gdpr_consent_text', e.target.value)} rows={2} placeholder="I agree to the processing of my personal data in accordance with the Privacy Policy." />
        </Field>
      )}
      <Field label="Data Retention (days, 0 = keep forever)">
        <Inp type="number" value={form.data_retention_days} onChange={e => set('data_retention_days', +e.target.value)} />
        <p className="text-xs text-ink-500 mt-1 font-sans">Submissions older than this will be automatically deleted by a scheduled job</p>
      </Field>
      <Toggle
        checked={form.allow_data_deletion ?? false}
        onChange={v => set('allow_data_deletion', v)}
        label="Allow data deletion requests"
        sub="Enables the Anonymize button in the Submissions view"
      />
      <Toggle
        checked={form.encrypt_submissions ?? false}
        onChange={v => set('encrypt_submissions', v)}
        label="Encrypt submissions at rest"
        sub="Submission data is encrypted using AES-256 before storing in database"
      />
      {form.encrypt_submissions && (
        <InfoBox type="warn">
          Encryption key is set via <code className="font-mono text-xs">FORM_ENCRYPTION_KEY</code> in your <code className="font-mono text-xs">.env</code> file.
          Once enabled, existing submissions will NOT be retroactively encrypted.
        </InfoBox>
      )}
    </Card>
  );
};


// ─── EMBED ────────────────────────────────────────────────────────────────────

const CopyBtn: React.FC<{ text: string }> = ({ text }) => {
  const [copied, setCopied] = React.useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button onClick={copy} className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium font-sans border border-ink-200 rounded-lg hover:bg-ink-50 transition-colors flex-shrink-0 text-brand-500 bg-[#ed5a2912]">
      {copied ? <Check className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3 text-brand-500  " />}
      {copied ? "Copied!" : "Copy"}
    </button>
  );
};

const CodeBlock: React.FC<{ code: string; lang?: string }> = ({ code, lang }) => (
  <div className="relative">
    <div className="flex items-center justify-between px-3 py-1.5 bg-[#1e1e2e] rounded-t-lg">
      <span className="text-[10px] text-gray-400 font-mono">{lang ?? "code"}</span>
      <CopyBtn text={code} />
    </div>
    <pre className="bg-[#282838] text-green-300 text-[10px] md:text-[11px] font-mono p-4 rounded-b-lg overflow-x-auto whitespace-pre leading-relaxed border border-[#3a3a4a] w-full">{code}</pre>
  </div>
);

// ─── SNIPPET TAB ─────────────────────────────────────────────────────────────

const SHORTCODE_VIDEO_URL = "https://www.youtube.com/embed/YOUR_VIDEO_ID";

const ShortcodeVideoLink: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, []);

  // Lock body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const modal = (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 md:p-12">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsOpen(false)}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          />

          {/* Modal Container */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="relative w-full max-w-[900px] bg-white rounded-[20px] shadow-2xl overflow-hidden flex flex-col max-h-full"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <div>
                <h3 className="text-xl font-bold text-gray-900 font-sans">How to use Shortcode</h3>
                <p className="text-sm text-gray-500 font-sans mt-0.5">Quick video guide</p>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500"
                aria-label="Close modal"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Video Player */}
            <div className="relative w-full bg-black aspect-video flex-shrink-0">
              <iframe
                src={`${SHORTCODE_VIDEO_URL}?autoplay=1&mute=0`}
                title="Shortcode Guide"
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );

  return (
    <>
      <div className="mt-5 mb-2 flex justify-center">
        <button
          onClick={() => setIsOpen(true)}
          className="group inline-flex items-center gap-2 text-sm font-medium text-ink-600 hover:text-brand-600 transition-colors py-2 px-5 rounded-full hover:bg-brand-50/50 border border-transparent hover:border-brand-500/20"
        >
          <PlayCircle className="w-4 h-4 text-brand-500 group-hover:scale-110 transition-transform" />
          ▶ How to use Shortcode?
        </button>
      </div>
      {createPortal(modal, document.body)}
    </>
  );
};

const InlineSnippetTab: React.FC<{ form: Form; apiBase: string; embedKey: string }> = ({ form, apiBase, embedKey }) => {
  const embedJsSrc = `${apiBase}/embed/form-embed.js`;

  // Build the snippet — a data-attribute div + external script reference, no
  // unique id or global config object needed. form-embed.js scans the page for
  // every [data-mobimint-form] element, so this exact snippet is safe to paste
  // more than once on one page for different forms.
  // Using the hosted form-embed.js (rather than an inlined copy) means the
  // snippet always runs the latest engine and never has stale field-name issues.
  const buildSnippet = (primary = '#6366f1', radius = '8') => {
    const attrs = [
      'data-mobimint-form',
      `data-api-base="${apiBase}"`,
      `data-form-key="${embedKey}"`,
      `data-form-slug="${form.slug}"`,
      primary !== '#6366f1' ? `data-primary="${primary}"` : null,
      radius !== '8' ? `data-radius="${radius}"` : null,
    ].filter(Boolean).join(' ');

    return `<!-- Mobimint Form -->\n<div ${attrs}></div>\n<script src="${embedJsSrc}" async></script>`;
  };

  const [primary, setPrimary] = React.useState('#6366f1');
  const [radius, setRadius] = React.useState('8');
  const snippet = buildSnippet(primary, radius);

  return (
    <>
      {/* Header banner */}
      <div className="bg-gradient-to-br from-[#f0fdf4] to-[var(--brand-100)] border border-green-200 rounded-xl p-4 mb-5 font-sans">
        <p className="text-sm font-bold text-green-700 mb-1.5">📋 Universal Snippet — copy once, paste anywhere</p>
        <p className="text-xs text-green-800 leading-relaxed">
          Two tags — a config block and one script. Works on <strong>any website</strong>: Shopify, WordPress, Webflow, Wix, plain HTML.
          The form engine is loaded from your server so it's always up to date.
        </p>
      </div>

      {/* Quick customise */}
      <div className="bg-white border border-ink-200 rounded-xl p-4 mb-4">
        <p className="text-xs font-semibold text-ink-900 font-sans mb-3">⚙️ Customise before copying</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-[11px] text-ink-500 font-sans mb-1.5">Accent colour</label>
            <div className="flex items-center gap-2">
              <input type="color" value={primary} onChange={e => setPrimary(e.target.value)}
                className="h-9 w-9 rounded-lg border border-ink-200 cursor-pointer p-1 flex-shrink-0" />
              <input type="text" value={primary} onChange={e => setPrimary(e.target.value)}
                className="flex-1 px-3 py-2 text-xs font-mono border border-ink-200 rounded-lg bg-white focus:border-brand-500 outline-none" />
            </div>
          </div>
          <div>
            <label className="block text-[11px] text-ink-500 font-sans mb-1.5">Border radius (px)</label>
            <input type="number" value={radius} min="0" max="24" onChange={e => setRadius(e.target.value)}
              className="w-full px-3 py-2 text-xs border border-ink-200 rounded-lg bg-white focus:border-brand-500 outline-none" />
          </div>
        </div>
      </div>

      {/* The snippet itself */}
      <div className="mb-4">
        <div className="flex items-center justify-between px-3 py-2 bg-[#1e1e2e] rounded-t-xl">
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-gray-400 font-mono">html</span>
          </div>
          <CopyBtn text={snippet} />
        </div>
        <pre className="bg-[#282838] text-green-300 text-[11px] font-mono p-4 rounded-b-xl overflow-x-auto whitespace-pre-wrap leading-relaxed border border-[#3a3a4a]">{snippet}</pre>
      </div>

      {/* Big copy button */}
      <button
        onClick={() => navigator.clipboard.writeText(snippet)}
        className="w-full py-3 bg-brand-500 hover:bg-brand-600 text-white text-sm font-semibold font-sans rounded-xl transition-colors flex items-center justify-center gap-2 mb-5"
      >
        <Copy className="h-4 w-4" /> Copy snippet
      </button>

      {/* Platform instructions */}
      <div className="space-y-3">
        {[
          {
            platform: "Shopify",
            icon: "🛍️",
            bg: "bg-amber-50 border-amber-200",
            text: "text-amber-800",
            steps: [
              "Shopify Admin → Online Store → Themes → Customize",
              "Click Add block → Custom HTML",
              "Paste the snippet → Save",
            ],
          },
          {
            platform: "WordPress",
            icon: "🌐",
            bg: "bg-blue-50 border-blue-200",
            text: "text-blue-800",
            steps: [
              "Edit any page or post in Gutenberg",
              "Add block → Custom HTML",
              "Paste the snippet → Update/Publish",
            ],
          },
          {
            platform: "Webflow",
            icon: "⚡",
            bg: "bg-purple-50 border-purple-200",
            text: "text-purple-800",
            steps: [
              "Webflow Designer → + Add Element → Embed",
              "Paste the snippet → Save & Publish",
            ],
          },
          {
            platform: "Wix",
            icon: "✦",
            bg: "bg-green-50 border-green-200",
            text: "text-green-800",
            steps: [
              "Wix Editor → Add → Embed Code → Embed HTML",
              "Paste the snippet → Apply → Publish",
            ],
          },
          {
            platform: "Plain HTML",
            icon: "📄",
            bg: "bg-gray-50 border-gray-200",
            text: "text-gray-800",
            steps: [
              "Paste anywhere in your HTML file — inside <body>",
              "No other changes needed",
            ],
          },
        ].map(({ platform, icon, bg, text, steps }) => (
          <div key={platform} className={`border rounded-xl p-3 ${bg}`}>
            <p className={`text-xs font-semibold font-sans mb-1.5 ${text}`}>{icon} {platform}</p>
            <ol className={`text-[11px] font-sans space-y-0.5 ${text} opacity-80`}>
              {steps.map((s, i) => (
                <li key={i}>{i + 1}. {s}</li>
              ))}
            </ol>
          </div>
        ))}
      </div>

      {/* CSS override note */}
      <div className="mt-4 bg-brand-100 border border-brand-500/20 rounded-xl p-3 text-xs text-brand-600 font-sans">
        <p className="font-semibold mb-1">💡 Want to override styles?</p>
        <p>All form elements have <code className="font-mono bg-brand-500/10 px-1 rounded">.mbf-*</code> CSS class names.
          Add your own CSS after the snippet to override anything — e.g.{" "}
          <code className="font-mono bg-brand-500/10 px-1 rounded">.mbf-btn-submit {"{ background: #your-color; }"}</code>
        </p>
      </div>

      <ShortcodeVideoLink />
    </>
  );
};

type MainTab = "mobile" | "website";
type WebsiteTab = "iframe" | "direct" | "shortcode";

export const EmbedTab: React.FC<{ form: Form }> = ({ form }) => {
  const [key, setKey] = React.useState<string>("");
  const [loading, setLoading] = React.useState(false);
  const [generated, setGenerated] = React.useState(false);
  const [mainTab, setMainTab] = React.useState<MainTab>("website");
  const [websiteTab, setWebsiteTab] = React.useState<WebsiteTab>("direct");
  const [err, setErr] = React.useState("");

  const apiBase = import.meta.env.VITE_API_URL ?? 'https://api-forms.mobimint.co';

  React.useEffect(() => {
    generateEmbedKey()
      .then(d => { setKey(d.public_key ?? ""); setGenerated(true); })
      .catch(() => { });
  }, []);

  const handleGenerate = async () => {
    setLoading(true); setErr("");
    try {
      const d = await generateEmbedKey();
      setKey(d.public_key ?? "");
      setGenerated(true);
    } catch (e: any) {
      setErr(e.message ?? "Failed to generate key");
    } finally {
      setLoading(false);
    }
  };

  // ── URLs ────────────────────────────────────────────────────────────────────
  const webviewUrl = `${apiBase}/form/${key}/${form.slug}`;
  const previewUrl = `${webviewUrl}?mobile=1`;
  const formJsSrc = `${apiBase}/embed/form.js`;
  const formEmbedJsSrc = `${apiBase}/embed/form-embed.js`;

  // ── Shared config: a data-attribute div, no unique id or global config object
  // needed — form.js/form-embed.js scan the page for every [data-mobimint-form]
  // element and initialize each independently, so this same snippet is safe to
  // paste multiple times on one page for different forms.
  const dataAttrs = `data-mobimint-form data-api-base="${apiBase}" data-form-key="${key}" data-form-slug="${form.slug}"`;

  // ── iFrame embed codes ──────────────────────────────────────────────────────
  // No Shopify snippet here — Shopify doesn't permit apps to hand merchants a
  // script/Liquid snippet to paste into a theme; see the Shopify Card above,
  // which points to the Mobimint Form theme app extension block instead.
  const wordpressIframe = `<!-- WordPress — HTML block in Gutenberg -->\n<div ${dataAttrs}></div>\n<script src="${formJsSrc}" async></script>`;
  const genericIframe = `<!-- Any website — paste before </body> -->\n<div ${dataAttrs}></div>\n<script src="${formJsSrc}" async></script>`;
  const plainIframe = `<!-- ⚠️ Plain iframe — {page_title} will NOT be captured -->\n<iframe\n  src="${webviewUrl}"\n  width="100%" height="600"\n  frameborder="0"\n  style="border-radius:12px;border:1px solid #e5e7eb"\n></iframe>`;

  // ── Direct embed codes ──────────────────────────────────────────────────────
  const wordpressDirect = `<!-- WordPress — Direct embed (no iframe) -->\n<div ${dataAttrs}></div>\n<script src="${formEmbedJsSrc}" async></script>`;
  const genericDirect = `<!-- Any website — Direct embed, paste before </body> -->\n<div ${dataAttrs}></div>\n<script src="${formEmbedJsSrc}" async></script>`;
  const liquidDirect = `{%- comment -%} Shopify Liquid — Direct embed {%- endcomment -%}\n<div ${dataAttrs}></div>\n<script src="${formEmbedJsSrc}" async></script>`;

  const cssOverride = `/* Paste in your site's CSS to match your brand */\n.mbf-input, .mbf-select, .mbf-textarea {\n  border-radius: 6px;\n  border-color: #e2e8f0;\n  font-size: 14px;\n}\n.mbf-btn-submit {\n  background: #your-brand-color;\n  border-radius: 6px;\n  font-weight: 600;\n}\n.mbf-label { font-size: 14px; font-weight: 500; color: #374151; }\n\n/* All available class names:\n   .mbf-wrap  .mbf-field  .mbf-label  .mbf-input  .mbf-select\n   .mbf-textarea  .mbf-option-item  .mbf-btn-submit  .mbf-btn-next\n   .mbf-btn-back  .mbf-rating  .mbf-star  .mbf-slider\n   .mbf-file-drop  .mbf-sig-wrap  .mbf-success  .mbf-error\n*/`;

  // ── Shortcode codes ─────────────────────────────────────────────────────────
  // WordPress shortcode — [mbf_form slug="..."] registered by a small plugin
  const wpShortcode = `[mbf_form slug="${form.slug}" key="${key}" api="${apiBase}"]`;

  const wpPluginCode = `<?php
/**
 * Plugin Name: Mobimint Forms Shortcode
 * Description: Renders Mobimint forms via [mbf_form] shortcode.
 * Version:     1.0
 */
add_shortcode('mbf_form', function($atts) {
    $a = shortcode_atts([
        'slug'   => '',
        'key'    => '',
        'api'    => '',
        'target' => '',
    ], $atts);
    if (!$a['slug'] || !$a['key'] || !$a['api']) return '<!-- mbf_form: missing attributes -->';
    $uid    = 'mbf-' . sanitize_html_class($a['slug']) . '-' . substr(md5(serialize($a)), 0, 6);
    $target = $a['target'] ?: "#{$uid}";
    $api    = esc_url(rtrim($a['api'], '/'));
    $slug   = esc_attr($a['slug']);
    $key    = esc_attr($a['key']);
    $src    = $api . '/embed/form-embed.js';
    return <<<HTML
<div id="{$uid}"></div>
<script>
window.MobimintFormConfig = {
  apiBase: '{$api}',
  formKey: '{$key}',
  slug:    '{$slug}',
  target:  '{$target}',
};
</script>
<script src="{$src}" async></script>
HTML;
});`;

  // Shopify section schema (for theme app extension / .liquid section file)
  const shopifySection = `{% comment %} sections/mbf-form.liquid {% endcomment %}
<div id="mbf-{{ section.id }}"></div>
<script>
window.MobimintFormConfig = {
  apiBase: '${apiBase}',
  formKey: '${key}',
  slug:    '${form.slug}',
  target:  '#mbf-{{ section.id }}',
};
</script>
<script src="${formEmbedJsSrc}" async></script>

{% schema %}
{
  "name": "Mobimint Form",
  "settings": [
    { "type": "text", "id": "form_slug", "label": "Form Slug", "default": "${form.slug}" }
  ],
  "presets": [{ "name": "Mobimint Form" }]
}
{% endschema %}`;

  // Webflow embed widget code
  const webflowCode = `<!-- Webflow: paste inside an Embed element -->\n<div data-mobimint-form data-api-base="${apiBase}" data-form-key="${key}" data-form-slug="${form.slug}"></div>\n<script src="${formEmbedJsSrc}"></script>`;

  // Wix Velo (custom element / HTML embed)
  const wixCode = `<!-- Wix: add an HTML iframe embed or use Velo -->\n<!-- Option 1: HTML Embed (simplest) -->\n<div data-mobimint-form data-api-base="${apiBase}" data-form-key="${key}" data-form-slug="${form.slug}"></div>\n<script src="${formEmbedJsSrc}"></script>`;

  // ── Mobile codes ────────────────────────────────────────────────────────────
  const rnCode = `// Install:  npm install react-native-webview\nimport { WebView } from 'react-native-webview';\n\n<WebView\n  source={{ uri: \`${webviewUrl}\` }}\n  style={{ flex: 1 }}\n  javaScriptEnabled\n  onMessage={event => {\n    const msg = JSON.parse(event.nativeEvent.data);\n    if (msg.type === 'submitted') navigation.goBack();\n  }}\n/>`;

  const flutterCode = `// pubspec.yaml — flutter_inappwebview: ^6.0.0\nimport 'package:flutter_inappwebview/flutter_inappwebview.dart';\n\nInAppWebView(\n  initialUrlRequest: URLRequest(\n    url: Uri.parse('${webviewUrl}'),\n  ),\n  onWebViewCreated: (controller) {\n    controller.addJavaScriptHandler(\n      handlerName: 'onSubmitted',\n      callback: (args) => Navigator.pop(context),\n    );\n  },\n)`;

  // ── Tab button helper ───────────────────────────────────────────────────────
  const MainTabBtn: React.FC<{ id: MainTab; icon: React.ReactNode; label: string }> = ({ id, icon, label }) => (
    <button
      onClick={() => setMainTab(id)}
      className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-medium font-sans transition-colors ${mainTab === id ? "bg-white text-brand-600 shadow-sm" : "text-ink-500 hover:text-ink-900"
        }`}
    >
      {icon} {label}
    </button>
  );

  const WebTabBtn: React.FC<{ id: WebsiteTab; icon: React.ReactNode; label: string; badge?: string }> = ({ id, icon, label, badge }) => (
    <button
      onClick={() => setWebsiteTab(id)}
      className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium font-sans transition-colors ${websiteTab === id ? "bg-white text-brand-600 shadow-sm" : "text-ink-500 hover:text-ink-900"
        }`}
    >
      {icon} {label}
      {badge && <span className="bg-green-100 text-green-700 text-[9px] font-bold px-1.5 py-0.5 rounded-full">{badge}</span>}
    </button>
  );

  return (
    <>
      {/* ── Public API Key ─────────────────────────────────────────────────── */}
      <Card title="Public API Key" sub="One key per store — safe to put in your website or mobile app. Never exposes admin access.">
        {err && <InfoBox type="warn">{err}</InfoBox>}
        {generated && key ? (
          <>
            <InfoBox type="success">✓ Key generated and active</InfoBox>
            <div className="flex items-center gap-2 p-3 bg-[#1e1e2e] rounded-xl mb-3">
              <code className="text-green-300 text-xs font-mono flex-1 break-all">{key}</code>
              <CopyBtn text={key} />
            </div>
            <p className="text-xs text-ink-500 font-sans">This key identifies your store. Use it in all embed methods below.</p>
          </>
        ) : (
          <div className="text-center py-6">
            <p className="text-sm text-ink-500 font-sans mb-4">Generate your store's public key to start embedding forms.</p>
            <button onClick={handleGenerate} disabled={loading}
              className="flex items-center gap-2 px-5 py-2.5 bg-brand-500 text-white rounded-xl text-sm font-medium font-sans hover:bg-brand-600 mx-auto disabled:opacity-60">
              {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : null}
              {loading ? "Generating..." : "Generate Public Key"}
            </button>
          </div>
        )}
      </Card>

      {/* ── Form Details ───────────────────────────────────────────────────── */}
      {form.id && (
        <Card title="Form Details">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs font-sans">
            <div>
              <p className="text-ink-500 mb-1">Form ID</p>
              <div className="flex items-center gap-2">
                <code className="font-mono text-ink-900 bg-ink-50 px-2 py-1 rounded">{form.id}</code>
                <CopyBtn text={String(form.id)} />
              </div>
            </div>
            <div>
              <p className="text-ink-500 mb-1">Form Slug</p>
              <div className="flex items-center gap-2">
                <code className="font-mono text-ink-900 bg-ink-50 px-2 py-1 rounded">{form.slug}</code>
                <CopyBtn text={form.slug ?? ''} />
              </div>
            </div>
          </div>
          {/* {key && (
            <div className="mt-3">
              <p className="text-ink-500 mb-1 text-xs">Form URL (WebView / iframe src)</p>
              <div className="flex items-center gap-2">
                <code className="font-mono text-[11px] text-brand-600 bg-brand-100 px-2 py-1.5 rounded flex-1 break-all">{webviewUrl}</code>
                <CopyBtn text={webviewUrl} />
                <a href={previewUrl} target="_blank" rel="noreferrer" className="p-1.5 border border-ink-200 rounded-lg hover:bg-ink-50">
                  <ExternalLink className="h-3.5 w-3.5 text-ink-500" />
                </a>
              </div>
            </div>
          )} */}
        </Card>
      )}

      {/* ── All embed codes (only when key exists) ─────────────────────────── */}
      {key && (
        <>
          {/* ── Main tab: Mobile | Website ────────────────────────────────── */}
          <div className="flex flex-col sm:flex-row gap-1 mb-4 bg-ink-50 p-1 rounded-xl">
            <MainTabBtn id="website" icon={<Globe className="h-3.5 w-3.5" />} label="Website" />
            <MainTabBtn id="mobile" icon={<Smartphone className="h-3.5 w-3.5" />} label="Mobile App" />

          </div>

          {/* ── MOBILE TAB ─────────────────────────────────────────────────── */}
          {mainTab === "mobile" && (
            <>
              <Card title="React Native" sub="Uses WebView — Stripe, file upload, and signature all work inside the page.">
                <CodeBlock code={rnCode} lang="tsx" />
                <div className="mt-3 bg-brand-100 border border-brand-500/20 rounded-lg p-3 text-xs text-brand-600 font-sans">
                  <p className="font-semibold mb-1">How it works:</p>
                  <p>1. WebView loads <code className="font-mono">/form/{"{key}"}/{"{slug}"}</code> from your server</p>
                  <p>2. Laravel renders the full HTML form with all fields</p>
                  <p>3. Customer fills and submits — <code className="font-mono">postMessage</code> fires on success</p>
                </div>
              </Card>
              <Card title="Flutter" sub="Uses flutter_inappwebview — same approach as React Native.">
                <CodeBlock code={flutterCode} lang="dart" />
              </Card>
            </>
          )}

          {/* ── WEBSITE TAB ────────────────────────────────────────────────── */}
          {mainTab === "website" && (
            <>
              {/* Website sub-tabs: iFrame | Direct | Shortcode */}
              <div className="flex flex-col sm:flex-row gap-1 mb-5 bg-ink-50 p-1 rounded-xl">
                {/* <WebTabBtn id="iframe" icon={<Layers className="h-3.5 w-3.5" />} label="iFrame Embed" /> */}
                <WebTabBtn id="direct" icon={<Code2 className="h-3.5 w-3.5" />} label="Direct Embed" badge="NEW" />
                {/* <WebTabBtn id="shortcode" icon={<Code className="h-3.5 w-3.5" />} label="Shortcode" badge="NEW" /> */}
              </div>

              {/* ════════════════════════════════════════════════════════════ */}
              {/* iFrame sub-tab                                               */}
              {/* ════════════════════════════════════════════════════════════ */}
              {websiteTab === "iframe" && (
                <>
                  <div className="bg-brand-100 border border-brand-500/20 rounded-xl p-4 mb-4 text-xs text-brand-600 font-sans">
                    <p className="font-bold mb-1.5">✨ Page title capture — fully automatic</p>
                    <p className="text-[var(--brand-600)] leading-relaxed">
                      The embed script (<code className="font-mono bg-brand-500/10 px-1 rounded">form.js</code>) runs on the merchant's page and reads{" "}
                      <code className="font-mono bg-brand-500/10 px-1 rounded">document.title</code> before the iframe loads — so{" "}
                      <code className="font-mono bg-brand-500/10 px-1 rounded">{"{page_title}"}</code> in emails always shows the correct page title.{" "}
                      <strong>No extra setup needed.</strong>
                    </p>
                  </div>

                  {/* Shopify requires storefront integrations to go through a Theme
                      App Extension block, not a pasted script/Liquid snippet — see
                      the note on the equivalent card in the "direct" sub-tab below. */}
                  <Card title="Shopify" sub="Add the Mobimint Form block from the theme editor — no code to paste.">
                    <div className="flex items-center justify-between gap-2 bg-ink-50 border border-ink-200 rounded-lg px-3 py-2">
                      <div className="min-w-0">
                        <p className="text-[10px] text-ink-500 font-sans">Form Slug</p>
                        <p className="text-xs font-mono text-ink-900 truncate">{form.slug}</p>
                      </div>
                      <CopyBtn text={form.slug ?? ""} />
                    </div>
                    <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700 font-sans">
                      <strong>How to add:</strong> Shopify Admin → Online Store → Themes → Customize → Add block → search "Mobimint Form" → paste the slug above into its settings.
                    </div>
                  </Card>
                  {/* 
                  <Card title="WordPress" sub="Paste into any page or post using the HTML block in Gutenberg.">
                    <CodeBlock code={wordpressIframe} lang="html" />
                    <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-700 font-sans">
                      <strong>How to add:</strong> Edit page → Add block → Custom HTML → paste above.
                    </div>
                  </Card> */}
                  {/* 
                  <Card title="Any Website" sub="Works with Wix, Squarespace, Webflow, or plain HTML.">
                    <CodeBlock code={genericIframe} lang="html" />
                  </Card> */}
                  {/* 
                  <Card title="Shopify Liquid (advanced)" sub="For Theme App Extensions or custom .liquid templates.">
                    <CodeBlock code={liquidIframe} lang="liquid" />
                  </Card> */}
                  {/* 
                  <Card title="Plain iframe (legacy)" sub="No page title capture. Use only if you cannot use the script embed.">
                    <CodeBlock code={plainIframe} lang="html" />
                    <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-700 font-sans">
                      ⚠️ With a plain iframe, <code className="font-mono">{"{page_title}"}</code> will be empty — the script embed above is recommended.
                    </div>
                  </Card> */}
                </>
              )}

              {/* ════════════════════════════════════════════════════════════ */}
              {/* Direct Embed sub-tab                                         */}
              {/* ════════════════════════════════════════════════════════════ */}
              {websiteTab === "direct" && (
                <>
                  {/* What is Direct Embed */}
                  <div className="bg-gradient-to-br from-[var(--brand-100)] to-[#f0fdf4] border border-brand-500/20 rounded-xl p-4 mb-5 font-sans">
                    <p className="text-sm font-bold text-brand-600 mb-2">🚀 Direct Embed — full control over design</p>
                    <p className="text-xs text-[var(--brand-600)] leading-relaxed mb-3">
                      Renders form fields <strong>directly inside your page's DOM</strong> — no iframe sandbox.
                      Your page fonts, colours, and CSS apply automatically. Override anything with <code className="font-mono bg-brand-500/10 px-1 rounded">.mbf-*</code> classes.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px]">
                      <div className="bg-white/70 rounded-lg p-3">
                        <p className="font-bold text-amber-600 mb-2">📦 iFrame Embed</p>
                        <ul className="space-y-1 text-amber-700">
                          <li>• Sandboxed — can't inherit styles</li>
                          <li>• Fixed height needed</li>
                          <li>• Zero CSS conflict risk</li>
                          <li>✅ Best for 3rd-party sites</li>
                        </ul>
                      </div>
                      <div className="bg-white/70 rounded-lg p-3">
                        <p className="font-bold text-green-700 mb-2">✨ Direct Embed</p>
                        <ul className="space-y-1 text-green-800">
                          <li>• Inherits all page styles</li>
                          <li>• Height auto-adjusts</li>
                          <li>• Override with .mbf-* CSS</li>
                          <li>✅ Best for your own site</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-5 text-xs text-amber-700 font-sans">
                    <strong>⚙️ One-time server step:</strong> Copy <code className="font-mono">form-embed.js</code> to{" "}
                    <code className="font-mono">public/embed/form-embed.js</code> so it's served at{" "}
                    <code className="font-mono break-all">{formEmbedJsSrc}</code>
                  </div>

                  {/* Shopify requires storefront integrations to go through a Theme
                      App Extension block, not a pasted script/Liquid snippet — see
                      the "Mobimint Form" block already published by this app
                      (extensions/form-selector). No code to paste: the merchant
                      adds the block from the theme editor's block picker and fills
                      in these two values in its settings panel. */}
                  <Card title="Shopify" sub="Add the Mobimint Form block from the theme editor — no code to paste.">
                    <ol className="text-xs text-ink-700 font-sans space-y-1.5 mb-3 list-decimal list-inside">
                      <li>Shopify Admin → Online Store → Themes → Customize</li>
                      <li>Add block (or Add section) → search "Mobimint Form"</li>
                      <li>Paste the two values below into the block's settings</li>
                    </ol>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between gap-2 bg-ink-50 border border-ink-200 rounded-lg px-3 py-2">
                        <div className="min-w-0">
                          <p className="text-[10px] text-ink-500 font-sans">Store Public Key</p>
                          <p className="text-xs font-mono text-ink-900 truncate">{key || "generating…"}</p>
                        </div>
                        <CopyBtn text={key} />
                      </div>
                      <div className="flex items-center justify-between gap-2 bg-ink-50 border border-ink-200 rounded-lg px-3 py-2">
                        <div className="min-w-0">
                          <p className="text-[10px] text-ink-500 font-sans">Form Slug</p>
                          <p className="text-xs font-mono text-ink-900 truncate">{form.slug}</p>
                        </div>
                        <CopyBtn text={form.slug ?? ""} />
                      </div>
                    </div>
                  </Card>

                  {/* <Card title="WordPress — Direct Embed" sub="Inherits your WordPress theme's typography automatically.">
                    <CodeBlock code={wordpressDirect} lang="html" />
                    <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-700 font-sans">
                      <strong>How to add:</strong> Edit page → Add block → Custom HTML → paste above.
                    </div>
                  </Card>

                  <Card title="Webflow" sub="Paste into a Webflow Embed element on any page.">
                    <CodeBlock code={webflowCode} lang="html" />
                    <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-700 font-sans">
                      <strong>How to add:</strong> Webflow Designer → Add Element → Embed → paste above → Save & Publish.
                    </div>
                  </Card>

                  <Card title="Wix" sub="Use Wix's HTML embed widget.">
                    <CodeBlock code={wixCode} lang="html" />
                    <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-700 font-sans">
                      <strong>How to add:</strong> Wix Editor → Add → Embed Code → Embed HTML → paste above.
                    </div>
                  </Card>

                  <Card title="Any Website — Direct Embed" sub="Works on any plain HTML site.">
                    <CodeBlock code={genericDirect} lang="html" />
                  </Card>

                  <Card title="Shopify Liquid — Direct Embed (advanced)" sub="For Theme App Extensions or custom .liquid section files.">
                    <CodeBlock code={liquidDirect} lang="liquid" />
                  </Card> */}

                  <Card title="Customise the form styles" sub="All elements have .mbf-* class names — paste overrides into your site CSS.">
                    <CodeBlock code={cssOverride} lang="css" />
                    <div className="mt-3 bg-brand-100 border border-brand-500/20 rounded-lg p-3 text-xs text-brand-600 font-sans">
                      <p className="font-semibold mb-1">💡 Tips</p>
                      <p>• All inputs already inherit <code className="font-mono">font-family</code> from your page.</p>
                      <p>• On Shopify — add CSS to <strong>Online Store → Themes → Edit Code → assets/custom.css</strong></p>
                      {/* <p>• On WordPress — add CSS to <strong>Appearance → Customize → Additional CSS</strong></p>
                      <p>• On Webflow — add CSS to <strong>Site Settings → Custom Code → &lt;head&gt; tag</strong></p> */}
                    </div>
                  </Card>
                </>
              )}

              {/* ════════════════════════════════════════════════════════════ */}
              {/* Shortcode / Self-contained snippet sub-tab                   */}
              {/* ════════════════════════════════════════════════════════════ */}
              {websiteTab === "shortcode" && (
                <InlineSnippetTab form={form} apiBase={apiBase} embedKey={key} />
              )}
            </>
          )}

          {/* ── Preview link (always visible) ──────────────────────────────── */}
          <div className="bg-ink-50 rounded-xl p-4 text-center mt-2">
            <p className="text-xs text-ink-500 font-sans mb-2">Preview your form in browser</p>
            <a href={previewUrl} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-ink-200 rounded-lg text-xs font-medium font-sans hover:bg-ink-50">
              <ExternalLink className="h-3.5 w-3.5" /> Open form preview
            </a>
          </div>
        </>
      )}
    </>
  );
};